import type { AppState, Profile, RuleDefinition, SourceReference, VehicleProfile } from "./types";

const REVIEWED = "2026-07-28";
export const precisionPackVersion = "precision-2026.07.28";

const official = (
  label: string,
  authority: string,
  url: string,
  verify = false,
  jurisdiction: SourceReference["jurisdiction"] = "federal",
): SourceReference => ({
  label,
  authority,
  url,
  jurisdiction,
  reviewedAt: REVIEWED,
  verify,
  reviewIntervalDays: verify ? 90 : 180,
});

const sources = {
  inspections: official(
    "Periodic vehicle-inspection intervals",
    "Federal Roads Office (FEDRO)",
    "https://www.astra.admin.ch/dam/astra/en/dokumente/abteilung_direktionsgeschaefteallgemein/strassen-verkehr/strassen_verkehr_2017.pdf.download.pdf/Roads%2520and%2520traffic%25202017%2520-%2520Developments%2C%2520facts%2C%2520figures.pdf",
  ),
  vehicleCanton: official(
    "Directory of cantonal road traffic offices",
    "Association of Cantonal Road Traffic Offices (asa)",
    "https://asa.ch/strassenverkehrsaemter/adressen/",
    true,
    "cantonal",
  ),
  vehicleRegistration: official(
    "Registering a vehicle",
    "Swiss Confederation · ch.ch",
    "https://www.ch.ch/en/vehicles-and-traffic/cars-and-other-vehicles/registering-and-deregistering-a-car/",
    true,
  ),
  vehicleImport: official(
    "Vehicle customs clearance after moving to Switzerland",
    "Federal Office for Customs and Border Security (FOCBS)",
    "https://www.bazg.admin.ch/en/vehicle-customs-clearance-switzerland-frequently-asked-questions",
  ),
  foreignLicence: official(
    "Exchange a foreign driving licence",
    "Cantonal Road Traffic Office Zurich",
    "https://www.zh.ch/de/mobilitaet/fuehrerausweis-fahren-lernen/auslaendischer-fuehrerausweis.html",
    true,
    "cantonal",
  ),
  permits: official(
    "Residence permits",
    "Swiss Confederation · ch.ch",
    "https://www.ch.ch/en/foreign-nationals-in-switzerland/entry-and-stay-in-switzerland/permits-for-living-in-switzerland/",
    true,
  ),
  permitC: official(
    "C permit for EU/EFTA nationals",
    "State Secretariat for Migration (SEM)",
    "https://www.sem.admin.ch/sem/en/home/themen/aufenthalt/eu_efta/ausweis_c_eu_efta.html",
    true,
  ),
  citizenship: official(
    "Ordinary naturalisation",
    "State Secretariat for Migration (SEM)",
    "https://www.sem.admin.ch/sem/en/home/integration-einbuergerung/schweizer-werden/ordentlich.html",
    true,
  ),
  citizenshipFaq: official(
    "Naturalisation requirements and language levels",
    "State Secretariat for Migration (SEM)",
    "https://www.sem.admin.ch/sem/en/home/integration-einbuergerung/schweizer-werden/faq.html",
    true,
  ),
  rav: official(
    "Registering as a jobseeker",
    "State Secretariat for Economic Affairs · arbeit.swiss",
    "https://www.arbeit.swiss/secoalv/en/home/menue/stellensuchende/arbeitslos-was-tun-/anmeldung.html",
  ),
  accidentEnd: official(
    "When accident cover begins and ends",
    "Federal Office of Public Health (FOPH)",
    "https://www.bag.admin.ch/en/when-does-accident-insurance-cover-begin-and-end",
  ),
  accidentHealth: official(
    "Suspending accident cover in health insurance",
    "Federal Office of Public Health (FOPH)",
    "https://www.bag.admin.ch/en/health-insurance-insured-persons-eligible-to-suspend-accident-cover",
  ),
  selfEmployedContributions: official(
    "Self-employed OASI, DI and EO contributions",
    "OASI/DI Information Centre",
    "https://www.ahv-iv.ch/p/2.02.e",
    true,
  ),
  selfEmployedAccident: official(
    "Accident insurance for self-employed people",
    "Swiss National Accident Insurance Fund (Suva)",
    "https://www.suva.ch/de-ch/versicherung/unfallversicherung/abklaerung-selbststaendig-oder-unselbststaendig/entscheid",
    true,
  ),
  premiumReduction: official(
    "Health-insurance costs and premium reductions",
    "Swiss Confederation · ch.ch",
    "https://www.ch.ch/en/health/health-insurance/health-insurance-costs/",
    true,
    "cantonal",
  ),
  healthEnrollment: official(
    "Working in Switzerland as a foreign national",
    "Swiss Confederation · ch.ch",
    "https://www.ch.ch/en/work/working-in-switzerland-as-a-foreign-national/",
  ),
  crossBorderHealth: official(
    "Health insurance for cross-border commuters",
    "Federal Office of Public Health (FOPH)",
    "https://www.bag.admin.ch/en/health-insurance-cross-border-commuters-working-in-switzerland",
    true,
  ),
  taxSource: official(
    "Subsequent ordinary assessment for tax at source",
    "Federal Tax Administration (FTA)",
    "https://www.estv.admin.ch/dam/estv/de/dokumente/dbst/kreisschreiben/dbst-ks-2019-1-045-d-de.pdf.download.pdf/dbst-ks-2019-1-045-d-de.pdf",
    true,
  ),
  pillar3a: official(
    "Third pillar limits and eligibility",
    "Swiss Confederation · ch.ch",
    "https://www.ch.ch/en/retirement/old-age-pension/the-3rd-pillar/",
  ),
  pillar3aCatchup: official(
    "Retroactive Pillar 3a contributions",
    "Federal Social Insurance Office (FSIO)",
    "https://www.bsv.admin.ch/en/newnsb/uvIC0mFBWEozIsXH-rb49",
  ),
  ahv: official(
    "Request an individual-account statement",
    "OASI/DI Information Centre",
    "https://www.ahv-iv.ch/en/Forms/Statement-of-the-individual-account",
  ),
  pillar2: official(
    "Occupational pension thresholds",
    "Federal Social Insurance Office (FSIO)",
    "https://www.bsv.admin.ch/en/old-age-provision-occupational-benefits-plan",
  ),
  retirement: official(
    "Swiss old-age pension and the first pillar",
    "Swiss Confederation · ch.ch",
    "https://www.ch.ch/en/retirement/old-age-pension/the-first-pillar",
  ),
  retirementForms: official(
    "OASI old-age pension forms",
    "OASI/DI Information Centre",
    "https://www.ahv-iv.ch/en/Forms/Forms/Benefits-of-AHV",
  ),
  nonEmployedAhv: official(
    "Non-employed and student OASI contribution guidance",
    "OASI/DI Information Centre",
    "https://www.ahv-iv.ch/en/Leaflets/Contributions-OASI-DI-IC",
    true,
  ),
  eoService: official(
    "Digital EO claim process for service from 2026",
    "OASI/DI Information Centre",
    "https://www.ahv-iv.ch/de/Sozialversicherungen/Leistungen-der-EO-MSE-EAE-BUE-AdopE/Digitales-EO-Anmeldungsverfahren-ab-2026",
    true,
  ),
  careCredits: official(
    "OASI care credits",
    "OASI/DI Information Centre",
    "https://www.ahv-iv.ch/en/Social-insurances/Glossary/term/betreuungsgutschriften",
    true,
  ),
  familyAllowances: official(
    "Family allowances",
    "OASI/DI Information Centre",
    "https://www.ahv-iv.ch/en/social-insurances/family-allowance-fz",
    true,
  ),
  compensationOffices: official(
    "Cantonal compensation offices",
    "OASI/DI Information Centre",
    "https://www.ahv-iv.ch/en/Contacts/Cantonal-compensation-offices",
    true,
    "cantonal",
  ),
  liability: official(
    "Personal liability insurance",
    "Swiss Confederation · ch.ch",
    "https://www.ch.ch/en/insurance/personal-liability-insurance/",
    true,
  ),
  rent: official(
    "Current mortgage reference rate and publication dates",
    "Federal Housing Office (FHO)",
    "https://www.bwo.admin.ch/de/referenzzinssatz",
    true,
  ),
  moving: official(
    "Notification of departure and registration",
    "Swiss Confederation · ch.ch",
    "https://www.ch.ch/en/housing/moving/notification-of-departure-and-registration/",
    true,
  ),
  birthRegistration: official(
    "Registering a birth",
    "Swiss Confederation · ch.ch",
    "https://www.ch.ch/en/family-and-partnership/maternity-and-paternity/pregnancy-and-birth/registering-a-birth",
  ),
  pregnancyWork: official(
    "Pregnancy, maternity leave and work",
    "Swiss Confederation · ch.ch",
    "https://www.ch.ch/en/family-and-partnership/work-and-family/pregnancy--maternity-leave-and-work",
    true,
  ),
  adoptionLeave: official(
    "Adoption leave",
    "Swiss Confederation · ch.ch",
    "https://www.ch.ch/en/family-and-partnership/adoption/adoption-leave",
    true,
  ),
  parentalResponsibility: official(
    "Parental responsibility",
    "Swiss Confederation · ch.ch",
    "https://www.ch.ch/en/family-and-partnership/maternity-and-paternity/pregnancy-and-birth/parental-responsibility/",
    true,
  ),
  leaving: official(
    "Preparing to move abroad",
    "Federal Department of Foreign Affairs (FDFA)",
    "https://www.eda.admin.ch/en/preparations-for-a-stay-abroad-emigration-and-repatriation",
    true,
  ),
  healthAbroad: official(
    "Health-insurance obligation when living abroad",
    "Federal Office of Public Health (FOPH)",
    "https://www.bag.admin.ch/en/health-insurance-requirement-to-obtain-insurance",
    true,
  ),
  realEstateTax: official(
    "Taxation of real estate",
    "Swiss Confederation · ch.ch",
    "https://www.ch.ch/en/taxes-and-finances/types-of-taxation/taxation-of-real-estate",
    true,
  ),
  homeOwnership: official(
    "Forms of homeownership",
    "Swiss Confederation · ch.ch",
    "https://www.ch.ch/en/housing/homeownership/buying-property/what-is-the-best-form-of-homeownership-for-you",
    true,
  ),
  buildingsProgramme: official(
    "The Buildings Programme",
    "Swiss Federal Office of Energy (SFOE)",
    "https://www.bfe.admin.ch/bfe/en/home/promotion/energy-efficiency/buildings-programme.html",
    true,
  ),
  fatca: official(
    "Switzerland–US FATCA guidance",
    "Federal Tax Administration (FTA)",
    "https://www.estv.admin.ch/en/administrative-assistance-in-accordance-with-fatca",
    true,
  ),
  usaTaxForms: official(
    "Switzerland–USA tax forms and W-8BEN",
    "Federal Tax Administration (FTA)",
    "https://www.estv.admin.ch/de/usa",
    true,
  ),
  da1: official(
    "DA-1 and R-US 164 forms",
    "Federal Tax Administration (FTA)",
    "https://www.estv.admin.ch/de/formulare-wegleitungen-direkte-bundessteuer",
    true,
    "cantonal",
  ),
  w8ben: official(
    "Instructions for Form W-8BEN",
    "US Internal Revenue Service",
    "https://www.irs.gov/instructions/iw8",
    true,
  ),
  professionalTrading: official(
    "Circular 36: professional securities trading",
    "Federal Tax Administration (FTA)",
    "https://www.estv.admin.ch/dam/de/sd-web/oOz28af293pZ/dbst-ks-2012-1-036-d-de.pdf",
    true,
  ),
  investorProtection: official(
    "Investor protection and authorised institutions",
    "Swiss Financial Market Supervisory Authority (FINMA)",
    "https://www.finma.ch/en/finma-public/authorised-institutions-individuals-and-products/",
    true,
  ),
  patientDirective: official(
    "Patient directive",
    "Swiss Confederation · ch.ch",
    "https://www.ch.ch/de/gesundheit/patientenverfugung",
    true,
  ),
  publicAuthorities: official(
    "Cantonal and communal authority directory",
    "Swiss Confederation · ch.ch",
    "https://www.ch.ch/en/safety-and-justice/addresses-of-administrative-authorities",
    true,
    "cantonal",
  ),
  compulsorySchool: official(
    "Organisation of compulsory education",
    "Swiss Conference of Cantonal Ministers of Education (EDK)",
    "https://edk.ch/en/education-system-ch/compulsory/organisation-of-compulsory-education",
    true,
    "cantonal",
  ),
};

function parse(value: string): Date {
  return new Date(`${value}T12:00:00`);
}

function iso(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function addDays(value: string, days: number): string {
  const date = parse(value);
  date.setDate(date.getDate() + days);
  return iso(date);
}

function addYears(value: string, years: number): string {
  const date = parse(value);
  date.setFullYear(date.getFullYear() + years);
  return iso(date);
}

function money(value: number): string {
  return new Intl.NumberFormat("en-CH", {
    style: "currency",
    currency: "CHF",
    maximumFractionDigits: 0,
  }).format(value);
}

function hasEmployeeContract(profile: Profile): boolean {
  return profile.employment === "employed" || profile.hasEmploymentContract === "yes";
}

function earnsFromWork(profile: Profile): boolean {
  return hasEmployeeContract(profile) || profile.employment === "self-employed";
}

function employmentAwareProfile(input: Profile): Profile {
  const records = input.employers?.filter((employer) =>
    employer.weeklyHours !== undefined
    || employer.annualGrossIncome !== undefined
    || employer.employmentEndDate
    || employer.pensionFundMember !== undefined
    || Boolean(employer.label?.trim())) ?? [];
  if (!records.length) return input;

  const hours = records
    .map((employer) => employer.weeklyHours)
    .filter((value): value is number => value !== undefined);
  const incomes = records
    .map((employer) => employer.annualGrossIncome)
    .filter((value): value is number => value !== undefined);
  const ending = records
    .filter((employer) => employer.employmentEndDate)
    .sort((a, b) => (a.employmentEndDate ?? "").localeCompare(b.employmentEndDate ?? ""))[0];
  const completeIncomePortfolio = incomes.length === records.length;

  return {
    ...input,
    hasEmploymentContract: "yes",
    employerCount: records.length,
    weeklyHours: hours.length ? Math.max(...hours) : input.weeklyHours,
    annualGrossIncome: completeIncomePortfolio
      ? incomes.reduce((sum, value) => sum + value, 0)
      : input.annualGrossIncome,
    highestAnnualIncomeFromOneEmployer: incomes.length
      ? Math.max(...incomes)
      : input.highestAnnualIncomeFromOneEmployer,
    pensionFundMember: records.length === 1
      ? records[0].pensionFundMember ?? input.pensionFundMember
      : input.pensionFundMember,
    employmentEndDate: ending?.employmentEndDate ?? input.employmentEndDate,
    employmentEndPlan: ending?.afterEnd ?? input.employmentEndPlan,
  };
}

function rule(
  id: string,
  input: Omit<RuleDefinition, "id" | "recurrence" | "timing"> & {
    dueDate: string;
    dueLabel: string;
    confidence?: "exact" | "forecast" | "review-window" | "needs-input";
    windowStart?: string;
  },
): RuleDefinition {
  const { dueDate, dueLabel, confidence = "exact", windowStart, ...rest } = input;
  return {
    id,
    contentVersion: precisionPackVersion,
    recurrence: "once",
    timing: { type: "fixed", date: dueDate, windowStart, label: dueLabel, confidence },
    ...rest,
  };
}

function annualRule(
  id: string,
  month: number,
  day: number,
  input: Omit<RuleDefinition, "id" | "recurrence" | "timing"> & {
    dueLabel: string;
    windowStartMonth?: number;
  },
): RuleDefinition {
  const { dueLabel, windowStartMonth, ...rest } = input;
  return {
    id,
    contentVersion: precisionPackVersion,
    recurrence: "annual",
    timing: {
      type: "annual",
      month,
      day,
      windowStartMonth,
      label: dueLabel,
      confidence: "review-window",
    },
    ...rest,
  };
}

function vehicleName(vehicle: VehicleProfile): string {
  return vehicle.label?.trim() || (vehicle.kind === "motorbike" ? "motorbike" : "car");
}

function vehicleRules(profile: Profile, today: string): RuleDefinition[] {
  if (profile.livesInSwitzerland === "no") return [];
  const selectedKinds = profile.vehicle === "both"
    ? ["car", "motorbike"] as const
    : profile.vehicle === "car" || profile.vehicle === "motorbike"
      ? [profile.vehicle] as const
      : [];
  const vehicles: VehicleProfile[] = profile.vehicles?.length
    ? profile.vehicles
    : selectedKinds.map((kind) => ({ id: `profile-${kind}`, kind }));

  const vehicleJourneys = vehicles.flatMap((vehicle) => {
    const name = vehicleName(vehicle);
    const result: RuleDefinition[] = [];
    if (!vehicle.firstRegistrationDate && !vehicle.officialInspectionDate && !vehicle.lastInspectionDate) {
      result.push(rule(`vehicle-details-${vehicle.id}`, {
        title: `Add the dates for your ${name}`,
        domain: "Mobility",
        requirement: "recommended",
        summary: "Add first registration or the latest inspection so CH/OS can calculate the next MFK window.",
        why: "Inspection cycles depend on the vehicle’s stage. Without dates, a generic reminder would be misleading.",
        impact: "Unlocks an inspection forecast",
        effort: "3 minutes",
        dueDate: addDays(today, 7),
        dueLabel: "Complete the vehicle record",
        confidence: "needs-input",
        dateKind: "input",
        logic: "No inspection anchor date is recorded for this vehicle.",
        calculation: "Waiting for first registration, latest inspection, or an official cantonal appointment.",
        documents: ["Vehicle registration document", "Latest inspection report or cantonal invitation"],
        steps: [{ id: "profile", title: "Open Profile and add the vehicle dates" }],
        sources: [sources.inspections, sources.vehicleCanton],
      }));
    } else if (
      !vehicle.officialInspectionDate
      && (!vehicle.inspectionStage || vehicle.inspectionStage === "unknown")
    ) {
      result.push(rule(`vehicle-stage-${vehicle.id}`, {
        title: `Confirm the inspection history for your ${name}`,
        domain: "Mobility",
        requirement: "recommended",
        summary: "The dates are saved. CH/OS still needs to know which inspection stage the latest date represents before it can forecast the next MFK window.",
        why: "The federal rhythm differs before the first inspection, after the first inspection, and after later recurring inspections.",
        impact: "Unlocks a defensible inspection forecast",
        effort: "2 minutes",
        dueDate: addDays(today, 7),
        dueLabel: "Confirm the inspection stage",
        confidence: "needs-input",
        dateKind: "input",
        logic: "An inspection anchor is recorded, but its stage is missing or marked unknown.",
        calculation: "No MFK date is forecast until the inspection stage is confirmed.",
        documents: ["Vehicle registration document", "Latest inspection report or cantonal invitation"],
        steps: [{ id: "stage", title: "Open Profile and choose the matching inspection history" }],
        sources: [sources.inspections, sources.vehicleCanton],
        conceptId: "mfk",
      }));
    } else {
      let dueDate = vehicle.officialInspectionDate;
      let dueLabel = "Official cantonal appointment";
      let calculation = "Uses the appointment date sent by the cantonal road traffic office.";
      let confidence: "exact" | "forecast" = "exact";
      if (!dueDate && vehicle.inspectionStage === "never" && vehicle.firstRegistrationDate) {
        dueDate = addYears(vehicle.firstRegistrationDate, 5);
        dueLabel = "Expected first MFK window; the canton sets the appointment";
        calculation = `First registration ${vehicle.firstRegistrationDate} + 5 years. The first inspection can be scheduled between years 5 and 6.`;
        confidence = "forecast";
      } else if (!dueDate && vehicle.inspectionStage === "first" && vehicle.lastInspectionDate) {
        dueDate = addYears(vehicle.lastInspectionDate, 3);
        dueLabel = "Expected second MFK; verify the cantonal summons";
        calculation = `First periodic inspection ${vehicle.lastInspectionDate} + 3 years.`;
        confidence = "forecast";
      } else if (!dueDate && vehicle.inspectionStage === "repeat" && vehicle.lastInspectionDate) {
        dueDate = addYears(vehicle.lastInspectionDate, 2);
        dueLabel = "Expected recurring MFK; verify the cantonal summons";
        calculation = `Latest recurring inspection ${vehicle.lastInspectionDate} + 2 years.`;
        confidence = "forecast";
      }
      if (dueDate) {
        result.push(rule(`vehicle-mfk-${vehicle.id}`, {
          title: `${confidence === "exact" ? "Prepare" : "Plan"} the next MFK for your ${name}`,
          domain: "Mobility",
          requirement: "required",
          summary: confidence === "exact"
            ? `Your recorded cantonal appointment is ${dueDate}.`
            : `The federal interval points to ${dueDate}; the canton controls the appointment.`,
          why: "Cars and motorcycles follow a 5/6-year, then 3-year, then 2-year periodic inspection rhythm.",
          impact: "Keeps the vehicle road-legal",
          effort: "30–90 minutes",
          dueDate,
          dueLabel,
          confidence,
          dateKind: confidence === "exact" ? "deadline" : "forecast",
          logic: "The stage and dates in your vehicle record select the applicable federal interval.",
          calculation,
          documents: ["Vehicle registration document", "Cantonal invitation if received", "Maintenance and tyre records"],
          steps: [
            { id: "invitation", title: "Check the cantonal invitation or confirm the forecast with your road traffic office", conceptId: "mfk" },
            { id: "roadworthy", title: "Check tyres, lights, brakes, fluids and required equipment" },
            { id: "documents", title: "Prepare the registration document and appointment confirmation" },
            { id: "result", title: "Record the completed inspection date back in CH/OS" },
          ],
          sources: [sources.inspections, sources.vehicleCanton],
          conceptId: "mfk",
        }));
      }
    }

    if (vehicle.liabilityInsurance === "no") {
      result.push(rule(`vehicle-liability-${vehicle.id}`, {
        title: `Arrange liability cover before using your ${name}`,
        domain: "Mobility",
        requirement: "required",
        summary: "Motor-vehicle liability insurance is required for registration and road use.",
        why: "The insurer sends proof of cover to the cantonal road traffic office.",
        impact: "Required for legal registration",
        effort: "30–60 minutes",
        dueDate: today,
        dueLabel: "Before the vehicle is used",
        dateKind: "deadline",
        logic: "Your vehicle record says mandatory liability cover is not in place.",
        documents: ["Vehicle registration document", "Driver and keeper details"],
        steps: [
          { id: "quote", title: "Choose liability cover appropriate to the vehicle and keeper" },
          { id: "proof", title: "Have the insurer transmit proof to the canton" },
          { id: "confirm", title: "Confirm the registration certificate and plates are valid" },
        ],
        sources: [sources.vehicleRegistration],
      }));
    }

    if (vehicle.importedFromAbroad === "yes" && vehicle.customsDeclared !== "yes") {
      result.push(rule(`vehicle-customs-${vehicle.id}`, {
        title: `Contact Swiss customs about your imported ${name} now`,
        domain: "Mobility",
        requirement: "required",
        summary: "A vehicle brought as part of a move must be declared at the first border crossing; there is not a one-year customs grace period.",
        why: "Swiss plates can follow later, but customs declaration is a separate and earlier obligation.",
        impact: "Resolves a customs-compliance gap",
        effort: "30–90 minutes",
        dueDate: today,
        dueLabel: "Act now if the vehicle crossed the border undeclared",
        dateKind: "deadline",
        logic: "The vehicle is recorded as imported from abroad and customs declaration is not confirmed.",
        documents: ["Foreign registration document", "Purchase contract or invoice", "Identity document", "Any Form 15.25 or 15.30"],
        steps: [
          { id: "stop", title: "Do not assume the one-year plate window also applies to customs" },
          { id: "contact", title: "Contact FOCBS and explain when and how the vehicle entered" },
          { id: "clear", title: "Complete the customs route and obtain Form 13.20 A or other written decision" },
          { id: "save", title: "Keep the customs evidence for cantonal registration" },
        ],
        sources: [sources.vehicleImport],
      }));
    }

    if (
      vehicle.importedFromAbroad === "yes"
      && vehicle.swissRegistrationCompleted !== "yes"
      && profile.residenceStartDate
    ) {
      const plateDue = addYears(profile.residenceStartDate, 1);
      result.push(rule(`vehicle-swiss-plates-${vehicle.id}`, {
        title: `Complete Swiss registration for your imported ${name}`,
        domain: "Mobility",
        requirement: "required",
        summary: `Your recorded move date points to a Swiss-plate deadline around ${plateDue}. The canton confirms the exact process.`,
        why: "Foreign vehicles brought with a relocation generally need Swiss registration and plates no later than one year after the move.",
        impact: "Keeps the vehicle legal to use in Switzerland",
        effort: "1–3 hours",
        dueDate: plateDue,
        windowStart: addDays(plateDue, -60),
        dueLabel: "No later than one year after relocation; verify with the canton",
        confidence: "forecast",
        dateKind: "forecast",
        pastDueBehavior: "verify-now",
        logic: "Imported vehicle is recorded, but Swiss registration is not confirmed.",
        calculation: `${profile.residenceStartDate} + 1 year = ${plateDue}.`,
        documents: ["Customs Form 13.20 A or decision", "Foreign registration document", "Insurance confirmation", "Proof of residence"],
        steps: [
          { id: "customs", title: "Confirm customs clearance evidence is complete" },
          { id: "canton", title: "Request the exact registration checklist from the cantonal road office" },
          { id: "insurance", title: "Arrange Swiss liability-insurance confirmation" },
          { id: "plates", title: "Complete inspection, registration and plates as instructed" },
        ],
        sources: [sources.vehicleImport, sources.vehicleCanton],
      }));
    }

    if (vehicle.registrationCurrent === "no") {
      result.push(rule(`vehicle-registration-current-${vehicle.id}`, {
        title: `Correct the registration or address for your ${name}`,
        domain: "Mobility",
        requirement: "required",
        summary: "Your profile says the vehicle registration or keeper address is not current.",
        why: "The cantonal road traffic office needs current keeper and vehicle records.",
        impact: "Restores current registration records",
        effort: "15–45 minutes",
        dueDate: today,
        dueLabel: "Act now",
        dateKind: "deadline",
        logic: "Registration and address current is recorded as no.",
        documents: ["Vehicle registration document", "Identity and address evidence", "Insurance confirmation if requested"],
        steps: [
          { id: "office", title: "Open the competent cantonal road-office service" },
          { id: "change", title: "Submit the address or registration correction" },
          { id: "proof", title: "Save the updated registration document or confirmation" },
        ],
        sources: [sources.vehicleCanton, sources.vehicleRegistration],
      }));
    }
    return result;
  });

  if (
    profile.foreignDrivingLicence === "yes"
    && profile.foreignLicenceExchanged !== "yes"
    && profile.residenceStartDate
  ) {
    const exchangeDue = addYears(profile.residenceStartDate, 1);
    vehicleJourneys.push(rule("foreign-licence-exchange", {
      title: "Exchange your foreign driving licence before the Swiss driving window closes",
      domain: "Mobility",
      requirement: "required",
      summary: `Your residence start date points to a 12-month exchange date around ${exchangeDue}. Rules for professional driving and required tests can be earlier or different.`,
      why: "After the ordinary 12-month window, a foreign licence no longer authorises driving in Switzerland until it is exchanged.",
      impact: "Preserves legal driving access",
      effort: "1–3 hours",
      dueDate: exchangeDue,
      windowStart: addDays(exchangeDue, -90),
      dueLabel: "Apply before 12 months of Swiss residence; canton verifies",
      confidence: "forecast",
      dateKind: "forecast",
      pastDueBehavior: "verify-now",
      logic: "A foreign licence is recorded, exchange is not confirmed, and a Swiss residence start date is available.",
      calculation: `${profile.residenceStartDate} + 1 year = ${exchangeDue}.`,
      documents: ["Original foreign licence", "Identity and permit", "Passport photo", "Eye-test evidence", "Cantonal application"],
      steps: [
        { id: "canton", title: "Check your canton's exchange form and country-specific test rules" },
        { id: "eye", title: "Arrange the required eyesight test" },
        { id: "apply", title: "Submit the exchange application before the deadline" },
        { id: "confirm", title: "Do not drive after the window unless your authority confirms validity" },
      ],
      sources: [sources.foreignLicence, sources.vehicleCanton],
    }));
  }

  return vehicleJourneys;
}

function residenceRules(profile: Profile, today: string): RuleDefinition[] {
  const result: RuleDefinition[] = [];
  const crossBorder = profile.livesInSwitzerland === "no" || profile.permitType === "G";
  const crossBorderHealthPending = !profile.crossBorderHealthRoute
    || profile.crossBorderHealthRoute === "pending";
  if (
    crossBorder
    && hasEmployeeContract(profile)
    && crossBorderHealthPending
    && profile.crossBorderHealthResolved !== "yes"
  ) {
    const start = profile.swissWorkStartDate;
    const dueDate = start ? addDays(start, 92) : addDays(today, 7);
    result.push(rule("cross-border-health-choice", {
      title: start
        ? "Complete your cross-border health-insurance choice"
        : "Add your Swiss work start date and confirm cross-border health cover",
      domain: "Health",
      requirement: "required",
      summary: start
        ? `Your recorded Swiss work start points to a three-month decision window around ${dueDate}.`
        : "G-permit and other cross-border workers need a country-specific insurance decision; no tacit choice should be assumed.",
      why: "Swiss insurance is the default for many cross-border workers, while eligible residents of neighbouring countries may formally request an exemption.",
      impact: "Prevents uninsured treatment costs or an invalid assumed exemption",
      effort: "30–90 minutes",
      dueDate,
      windowStart: start,
      dueLabel: start ? "Within three months of Swiss work starting" : "Complete the cross-border module",
      confidence: start ? "forecast" : "needs-input",
      dateKind: start ? "forecast" : "input",
      logic: "You live outside Switzerland or hold a G permit, have employment, and have not confirmed the applicable health-insurance route.",
      calculation: start ? `${start} + approximately 3 months = ${dueDate}.` : "Waiting for the Swiss employment start date.",
      documents: ["Employment contract", "G permit if applicable", "Residence-country insurance evidence", "Family-member details"],
      steps: [
        { id: "country", title: "Check the FOPH rule for your residence country and nationality" },
        { id: "authority", title: "Contact the authority in your Swiss work canton" },
        { id: "choice", title: "Enroll in Swiss insurance or submit a formal exemption request if eligible" },
        { id: "proof", title: "Keep the authority's written decision and effective date" },
      ],
      sources: [sources.crossBorderHealth],
    }));
  }
  if (crossBorder) return result;

  if (
    profile.residency !== "swiss"
    && profile.permitGoal === "renew"
    && !profile.permitExpiry
  ) {
    result.push(rule("permit-renewal-details", {
      title: "Record your permit expiry and authority route",
      domain: "Residency",
      requirement: "recommended",
      summary: "CH/OS can prepare a renewal workflow without inventing a filing deadline once the expiry date is known.",
      why: "The date on the permit is a planning anchor. The responsible canton or commune and any authority notice establish the actual submission timing.",
      impact: "Unlocks an evidence-safe renewal plan",
      effort: "3 minutes",
      dueDate: addDays(today, 7),
      dueLabel: "Add the permit evidence when available",
      confidence: "needs-input",
      dateKind: "input",
      logic: "Permit renewal is the selected goal, but no permit expiry is recorded.",
      documents: ["Current permit", "Authority renewal notice if received"],
      steps: [
        { id: "expiry", title: "Add the expiry date printed on the permit" },
        { id: "route", title: "Confirm the competent canton or commune route" },
      ],
      sources: [sources.permits],
    }));
  }

  if (profile.residency !== "swiss" && profile.permitGoal === "permit-c") {
    if (!profile.residenceStartDate || !profile.nationalityPath) {
      result.push(rule("permit-c-details", {
        title: "Add the dates needed to estimate your C-permit route",
        domain: "Residency",
        requirement: "recommended",
        summary: "Continuous residence and nationality route determine whether a five- or ten-year threshold may apply.",
        why: "Permit C is not based on permit type alone, and cantonal verification remains necessary.",
        impact: "Unlocks an eligibility forecast",
        effort: "3 minutes",
        dueDate: addDays(today, 7),
        dueLabel: "Complete the residency module",
        confidence: "needs-input",
        dateKind: "input",
        logic: "The profile is missing a continuous-residence start date or nationality route.",
        documents: ["Current permit", "Arrival or residence history"],
        steps: [{ id: "profile", title: "Add continuous residence and nationality route in Profile" }],
        sources: [sources.permitC, sources.permits],
      }));
    } else {
      const years = profile.nationalityPath === "five-year-c" ? 5 : 10;
      const dueDate = addYears(profile.residenceStartDate, years);
      result.push(rule("permit-c-eligibility", {
        title: "Verify your C-permit eligibility",
        domain: "Residency",
        requirement: "recommended",
        summary: `Your recorded route reaches its ${years}-year checkpoint on ${dueDate}.`,
        why: "Residence duration is only one condition. Continuity, integration and cantonal procedure still need confirmation.",
        impact: "May improve residence stability",
        effort: "1–3 hours",
        dueDate,
        windowStart: addDays(dueDate, -90),
        dueLabel: `${years}-year checkpoint; verify with your canton`,
        confidence: "forecast",
        dateKind: "forecast",
        pastDueBehavior: "verify-now",
        logic: `Your profile selects the ${years}-year route and records continuous residence from ${profile.residenceStartDate}.`,
        calculation: `${profile.residenceStartDate} + ${years} years = ${dueDate}.`,
        documents: ["Current permit", "Residence history", "Language evidence requested by the canton"],
        steps: [
          { id: "canton", title: "Check your cantonal migration office’s current C-permit conditions" },
          { id: "continuity", title: "Confirm uninterrupted residence and permit history" },
          { id: "language", title: "Check the language-certificate requirement" },
          { id: "apply", title: "Apply only when the authority confirms eligibility" },
        ],
        sources: [sources.permitC, sources.permits],
      }));
    }
  }

  if (profile.residency !== "swiss" && profile.permitGoal === "citizenship") {
    if (!profile.residenceStartDate) {
      result.push(rule("citizenship-details", {
        title: "Add your Swiss residence start date",
        domain: "Residency",
        requirement: "recommended",
        summary: "CH/OS can then estimate the federal ten-year naturalisation checkpoint.",
        why: "Cantonal and communal residence conditions must still be verified separately.",
        impact: "Unlocks a naturalisation readiness plan",
        effort: "2 minutes",
        dueDate: addDays(today, 7),
        dueLabel: "Complete the residency module",
        confidence: "needs-input",
        dateKind: "input",
        logic: "No continuous Swiss residence start date is recorded.",
        documents: ["Residence history"],
        steps: [{ id: "profile", title: "Add your continuous residence start date in Profile" }],
        sources: [sources.citizenship],
      }));
    } else {
      const dueDate = addYears(profile.residenceStartDate, 10);
      result.push(rule("citizenship-readiness", {
        title: "Build your naturalisation readiness file",
        domain: "Residency",
        requirement: "optional",
        summary: `Your basic federal ten-year checkpoint is ${dueDate}; local conditions can move the real application date.`,
        why: "Ordinary naturalisation normally also requires a C permit, three years in the last five, integration and local conditions.",
        impact: "Creates a verified route to an application",
        effort: "Several sessions",
        dueDate,
        windowStart: addDays(dueDate, -365),
        dueLabel: "Federal residence checkpoint; canton and commune must verify",
        confidence: "forecast",
        dateKind: "forecast",
        pastDueBehavior: "verify-now",
        logic: `Continuous residence is recorded from ${profile.residenceStartDate}. The age 8–18 double-count rule is not automatically modelled.`,
        calculation: `${profile.residenceStartDate} + 10 years = ${dueDate}, before local adjustments.`,
        documents: ["C permit", "Residence history", "Tax and debt confirmations", "Language certificate", "Integration evidence"],
        steps: [
          { id: "local", title: "Verify canton and commune residence-duration rules" },
          { id: "permit", title: "Confirm you will hold a C permit when applying" },
          { id: "language", title: "Reach at least B1 oral and A2 written, or verify accepted evidence" },
          { id: "standing", title: "Check taxes, debts and social-assistance conditions" },
          { id: "meeting", title: "Request the official checklist from your canton or commune" },
        ],
        sources: [sources.citizenship, sources.citizenshipFaq],
      }));
    }
  }
  return result;
}

function workAndInsuranceRules(profile: Profile, today: string): RuleDefinition[] {
  profile = employmentAwareProfile(profile);
  const result: RuleDefinition[] = [];
  const mixedLifeStage = profile.employment === "student"
    || profile.employment === "retired"
    || profile.employment === "caregiver"
    || profile.employment === "inactive";
  if (mixedLifeStage && profile.hasEmploymentContract === undefined) {
    result.push(rule("employment-contract-details", {
      title: "Tell CH/OS whether you also have an employment contract",
      domain: "Work",
      requirement: "recommended",
      summary: "A student, retired person, caregiver or person on a break may still be an employee for accident and pension rules.",
      why: "Primary life stage and employment-insurance status are not the same thing.",
      impact: "Prevents the wrong cover assumptions",
      effort: "1 minute",
      dueDate: addDays(today, 7),
      dueLabel: "Complete the work profile",
      confidence: "needs-input",
      dateKind: "input",
      logic: `Your primary status is “${profile.employment}”, but employment-contract status is unknown.`,
      documents: ["Employment contract, if any"],
      steps: [{ id: "contract", title: "Record whether you currently have an employment contract" }],
      sources: [sources.accidentHealth],
    }));
  }
  const employeeContract = hasEmployeeContract(profile);
  const atLeastEight = employeeContract && (profile.weeklyHours ?? 0) >= 8;
  if (employeeContract && (profile.weeklyHours === undefined || profile.accidentInHealthInsurance === undefined)) {
    result.push(rule("work-cover-details", {
      title: "Add the facts needed to check accident cover",
      domain: "Work",
      requirement: "recommended",
      summary: "Weekly hours with one employer and the accident component in basic health insurance determine whether CH/OS should flag a gap or duplicate cover.",
      why: "The same eight-hour threshold applies to working students and people whose primary life stage is not simply “employed”.",
      impact: "Unlocks a precise cover check",
      effort: "3 minutes",
      dueDate: addDays(today, 7),
      dueLabel: "Complete the work and insurance fields",
      confidence: "needs-input",
      dateKind: "input",
      logic: `An employment contract is recorded, but ${profile.weeklyHours === undefined ? "weekly hours" : "the health-insurance accident component"} is still unknown.`,
      documents: ["Employment contract or hours confirmation", "Basic health-insurance policy"],
      steps: [
        { id: "hours", title: "Record weekly hours with the same employer" },
        { id: "health", title: "Record whether accident is included in basic health insurance" },
      ],
      sources: [sources.accidentHealth],
    }));
  }
  if (employeeContract && profile.weeklyHours !== undefined) {
    if (atLeastEight && profile.accidentInHealthInsurance === "yes") {
      result.push(rule("accident-duplicate", {
        title: "Ask your health insurer to suspend duplicate accident cover",
        domain: "Health",
        requirement: "recommended",
        summary: "At eight or more hours per week with one employer, non-occupational accidents are normally covered through work.",
        why: "Removing the accident component from compulsory health insurance can reduce the premium while employer cover applies.",
        impact: "Avoids paying twice",
        effort: "15 minutes",
        dueDate: addDays(today, 14),
        dueLabel: "After obtaining written employer proof",
        dateKind: "review",
        logic: `You recorded ${profile.weeklyHours} hours per week and accident cover still included in basic insurance.`,
        documents: ["Employer confirmation", "Health-insurance policy"],
        steps: [
          { id: "proof", title: "Get written proof of non-occupational accident cover" },
          { id: "send", title: "Send the proof and suspension request to the health insurer" },
          { id: "premium", title: "Check the effective date and revised premium" },
        ],
        sources: [sources.accidentHealth],
      }));
    } else if (!atLeastEight && profile.accidentInHealthInsurance === "no") {
      result.push(rule("accident-gap", {
        title: "Add accident cover to compulsory health insurance",
        domain: "Health",
        requirement: "required",
        summary: "Below eight hours per week, employer cover generally excludes non-occupational accidents.",
        why: "Your profile indicates a possible gap for accidents away from work.",
        impact: "Closes a material insurance gap",
        effort: "15 minutes",
        dueDate: today,
        dueLabel: "Act now",
        dateKind: "deadline",
        logic: `You recorded ${profile.weeklyHours} hours per week and no accident component in basic insurance.`,
        documents: ["Health-insurance policy", "Employment-hours confirmation"],
        steps: [
          { id: "confirm", title: "Confirm the weekly-hours basis with your employer" },
          { id: "contact", title: "Ask the health insurer to add accident cover" },
          { id: "effective", title: "Confirm the effective date in writing" },
        ],
        sources: [sources.accidentHealth],
      }));
    }
  }

  if (profile.nextEvent === "job-change" && !profile.employmentEndDate) {
    result.push(rule("employment-end-date-details", {
      title: "Confirm the employment transition date when it becomes known",
      domain: "Work",
      requirement: "recommended",
      summary: "Your route is saved, but CH/OS will not calculate accident-cover or benefit dates from a guessed final salary-entitlement day.",
      why: "The last working day and the last day with entitlement to at least half salary can differ.",
      impact: "Unlocks reliable transition timing",
      effort: "3 minutes",
      dueDate: addDays(today, 7),
      dueLabel: profile.employmentEndDateCertainty === "ongoing" ? "Review when the transition becomes definite" : "Add the date when confirmed",
      confidence: "needs-input",
      dateKind: "input",
      logic: "A work change is selected, but the salary-entitlement end date is unknown or ongoing.",
      documents: ["Employment contract", "Termination or new-role confirmation if available"],
      steps: [{ id: "date", title: "Ask the employer for the final salary-entitlement date and record it in Profile" }],
      sources: [sources.accidentEnd, sources.rav],
    }));
  }

  const notSeekingWork = profile.employment === "retired"
    || profile.employment === "caregiver"
    || profile.employment === "inactive"
    || profile.nextEvent === "leaving"
    || profile.employmentEndPlan === "leaving-switzerland"
    || profile.employmentEndPlan === "new-swiss-job"
    || profile.employmentEndPlan === "not-working";
  if (
    (profile.employmentEndDate || profile.nextEvent === "job-change")
    && (!profile.employmentEndPlan || profile.employmentEndPlan === "unsure")
    && profile.employment !== "unemployed"
    && profile.nextEvent !== "leaving"
  ) {
    result.push(rule("employment-end-route", {
      title: "Tell CH/OS what happens after this employment ends",
      domain: "Work",
      requirement: "recommended",
      summary: "A new Swiss job, unemployment, cross-border job search and permanent departure lead to different actions.",
      why: "An end date alone is not enough to label RAV registration or job-search evidence as required.",
      impact: "Prevents the wrong employment-transition route",
      effort: "1 minute",
      dueDate: addDays(today, 7),
      dueLabel: "Complete the employment transition",
      confidence: "needs-input",
      dateKind: "input",
      logic: profile.employmentEndDate
        ? `Employment ends on ${profile.employmentEndDate}, but the next situation is not recorded.`
        : "A job change is recorded, but the route and last day of the current role are not confirmed.",
      documents: ["Termination notice or fixed-term contract", "Any follow-on employment contract"],
      steps: [{ id: "route", title: "Choose what happens after the employment end date in Profile" }],
      sources: [sources.rav, sources.accidentEnd],
    }));
  }
  const jobRisk = !notSeekingWork && (
    profile.employment === "unemployed"
    || profile.employmentEndPlan === "job-seeking-switzerland"
  );
  if (jobRisk) {
    if (profile.jobSearchStarted !== "yes") {
      result.push(rule("job-search-proof", {
        title: "Start and document your job search now",
        domain: "Work",
        requirement: "required",
        summary: "RAV can ask for evidence that applications began during the notice period.",
        why: "Missing search evidence can affect unemployment benefits.",
        impact: "Protects benefit eligibility",
        effort: "Ongoing",
        dueDate: today,
        dueLabel: "During the notice period; do not wait for the last workday",
        dateKind: "deadline",
        logic: "Your profile shows unemployment or a job change, but job-search evidence is not confirmed.",
        documents: ["Applications and responses", "Notice letter", "CV"],
        steps: [
          { id: "log", title: "Create a dated application log" },
          { id: "apply", title: "Begin suitable applications immediately" },
          { id: "evidence", title: "Keep adverts, applications and responses" },
        ],
        sources: [sources.rav],
      }));
    }
    if (profile.ravRegistered !== "yes") {
      result.push(rule("rav-registration", {
        title: "Register with RAV before benefits need to start",
        domain: "Work",
        requirement: "required",
        summary: "Register as soon as unemployment is foreseeable and no later than the first day benefits are claimed.",
        why: "RAV can support the job search during notice, and late registration can delay the process.",
        impact: "Starts employment-service and benefit administration",
        effort: "30–60 minutes",
        dueDate: profile.employmentEndDate || today,
        windowStart: today,
        dueLabel: profile.employmentEndDate ? `No later than the first benefit day after ${profile.employmentEndDate}` : "As soon as unemployment is foreseeable",
        dateKind: "deadline",
        logic: profile.employmentEndDate
          ? `Your recorded employment end date is ${profile.employmentEndDate}.`
          : "No employment end date is recorded, so CH/OS cannot calculate the first benefit day.",
        documents: ["OASI or health-insurance card", "ID or permit", "Job-search evidence", "Termination documents"],
        steps: [
          { id: "register", title: "Register through Job-Room or the competent RAV" },
          { id: "documents", title: "Prepare identity, permit and termination documents" },
          { id: "appointment", title: "Attend the first consultation and note monthly evidence rules" },
        ],
        sources: [sources.rav],
      }));
    }
    if (profile.employmentEndDate) {
      result.push(rule("employment-end-handover", {
        title: "Complete the pension, benefit and household handover",
        domain: "Work",
        requirement: "recommended",
        summary: "Choose the unemployment-fund route, protect Pillar 2 assets and reforecast cash needs before salary stops.",
        why: "RAV registration is only one part of a Swiss employment transition.",
        impact: "Prevents pension assets, benefit evidence and family cash flow from being left behind",
        effort: "45–90 minutes",
        dueDate: profile.employmentEndDate,
        windowStart: addDays(profile.employmentEndDate, -30),
        dueLabel: "Prepare before the final salary-entitlement date",
        dateKind: "review",
        logic: `Job-seeking in Switzerland is recorded after the employment end date ${profile.employmentEndDate}.`,
        documents: ["Termination and salary records", "Pension-fund instructions", "Unemployment-fund forms", "Household essential-cost estimate"],
        steps: [
          { id: "fund", title: "Choose an unemployment fund and list its evidence requirements" },
          { id: "pension", title: "Give the pension fund the correct vested-benefits destination if no new fund takes over" },
          { id: "payroll", title: "Collect final salary, holiday and employer-certificate evidence" },
          { id: "household", title: "Reforecast essential household cash flow and benefit continuity" },
        ],
        sources: [sources.rav, sources.pillar2, sources.accidentEnd],
      }));
    }
  }

  if (profile.employmentEndDate && profile.employmentEndPlan === "new-swiss-job") {
    result.push(rule("new-job-transition", {
      title: "Close the insurance and pension handover to your new job",
      domain: "Work",
      requirement: "recommended",
      summary: "Confirm the old and new employment dates, accident-cover handover and occupational-pension transfer.",
      why: "A job change is an administrative handover, not an unemployment route.",
      impact: "Prevents cover and pension-transfer gaps",
      effort: "30–60 minutes",
      dueDate: profile.employmentEndDate,
      windowStart: addDays(profile.employmentEndDate, -30),
      dueLabel: "Complete around the final workday",
      dateKind: "review",
      logic: "A follow-on Swiss job is recorded after the employment end date, so RAV actions are suppressed.",
      documents: ["Old and new employment contracts", "Pension transfer instructions", "Accident-cover confirmation"],
      steps: [
        { id: "dates", title: "Confirm the final salary-entitlement date and new start date" },
        { id: "pension", title: "Give the old fund the new pension-fund transfer details" },
        { id: "accident", title: "Confirm when new non-occupational accident cover begins" },
        { id: "payroll", title: "Check withholding tax, benefits and personal details with new payroll" },
      ],
      sources: [sources.accidentEnd, sources.pillar2],
    }));
  }

  if (profile.employmentEndDate && profile.employmentEndPlan === "cross-border-job-seeking") {
    result.push(rule("cross-border-job-search-route", {
      title: "Verify the cross-border unemployment route before the job ends",
      domain: "Work",
      requirement: "required",
      summary: "Residence country, last workplace and social-security coordination determine where unemployment registration and benefits are handled.",
      why: "A Swiss RAV registration should not be assumed from an employment end date when you live abroad.",
      impact: "Protects registration and benefit timing",
      effort: "45–90 minutes",
      dueDate: profile.employmentEndDate,
      windowStart: today,
      dueLabel: "Verify before the first day without work",
      dateKind: "deadline",
      logic: "Cross-border job-seeking is selected as the post-employment route.",
      documents: ["Employment termination", "G permit", "Residence evidence", "Contribution and salary records"],
      steps: [
        { id: "residence", title: "Contact the employment service in your country of residence" },
        { id: "swiss", title: "Ask the Swiss work-canton authority which documents it must issue" },
        { id: "records", title: "Request employment and contribution evidence before access ends" },
        { id: "health", title: "Confirm when cross-border health-insurance obligations change" },
      ],
      sources: [sources.rav, sources.crossBorderHealth],
    }));
  }

  if (profile.employmentEndDate && profile.employmentEndPlan === "not-working") {
    result.push(rule("career-break-transition", {
      title: "Set up the contribution and cover route for a period without work",
      domain: "Pension",
      requirement: "recommended",
      summary: "Confirm AHV contributions, accident cover, Pillar 2 assets and cash runway before the employment cover ends.",
      why: "A planned break is not unemployment, but it can still create contribution and insurance gaps.",
      impact: "Protects first-pillar years and essential cover",
      effort: "45–90 minutes",
      dueDate: profile.employmentEndDate,
      windowStart: addDays(profile.employmentEndDate, -30),
      dueLabel: "Complete by the final salary-entitlement date",
      dateKind: "review",
      logic: "A non-working period is recorded after employment, so RAV actions are suppressed.",
      documents: ["Employment-end confirmation", "AHV contribution evidence", "Pension transfer instructions", "Health policy"],
      steps: [
        { id: "ahv", title: "Confirm the non-employed AHV contribution route" },
        { id: "pension", title: "Choose the permitted vested-benefits destination" },
        { id: "accident", title: "Arrange accident cover after employer protection ends" },
        { id: "cash", title: "Update the cash plan for the break" },
      ],
      sources: [sources.nonEmployedAhv, sources.accidentEnd, sources.pillar2],
    }));
  }

  if (profile.employmentEndDate) {
    const coverEnd = addDays(profile.employmentEndDate, 31);
    result.push(rule("accident-after-employment", {
      title: "Secure accident cover after employment ends",
      domain: "Health",
      requirement: "required",
      summary: `Employer accident cover normally ends 31 days after salary entitlement, pointing to ${coverEnd} from your recorded end date.`,
      why: "This transition still matters when the job ends for retirement or another non-job-seeking life stage. If the recorded date is not the last day of entitlement to at least half salary, correct it before relying on the calculation.",
      impact: "Prevents an uninsured transition",
      effort: "20 minutes",
      dueDate: addDays(coverEnd, -7),
      dueLabel: `Confirm cover before the estimated end on ${coverEnd}`,
      confidence: "forecast",
      dateKind: "forecast",
      logic: "CH/OS uses the recorded employment end as a proxy for the last day of half-salary entitlement.",
      calculation: `${profile.employmentEndDate} + 31 days = ${coverEnd}. Interim cover may extend protection for up to six months.`,
      documents: ["Employer confirmation of the last salary-entitlement date", "Accident and health-insurance policies"],
      steps: [
        { id: "confirm-date", title: "Confirm the last day of entitlement to at least half salary" },
        { id: "next-cover", title: "Check whether a new employer or unemployment insurance takes over" },
        { id: "bridge", title: "If needed, arrange interim cover or restore health-insurance accident cover" },
        { id: "proof", title: "Save written confirmation of effective dates" },
      ],
      sources: [sources.accidentEnd, sources.accidentHealth],
    }));
  }

  if (profile.personalLiabilityCovered === "no") {
    result.push(rule("personal-liability-gap", {
      title: "Decide and arrange personal liability cover",
      domain: "Protection",
      requirement: "recommended",
      summary: "It is generally voluntary, but protects against large claims and landlords may require it.",
      why: "Your profile records no personal liability cover.",
      impact: "Protects household finances",
      effort: "30–60 minutes",
      dueDate: addDays(today, 30),
      dueLabel: "Review within a month",
      dateKind: "review",
      logic: "This is a recommendation, not a universal legal obligation.",
      documents: ["Existing policies", "Lease requirements", "Household details"],
      steps: [
        { id: "lease", title: "Check whether your lease requires proof" },
        { id: "scope", title: "Choose the insured household and deductible" },
        { id: "exclusions", title: "Check major exclusions before buying" },
      ],
      sources: [sources.liability],
    }));
  }
  if (profile.employment === "self-employed" && profile.selfEmployedStatusConfirmed !== "yes") {
    result.push(rule("self-employed-social-status", {
      title: "Confirm self-employed status with the compensation office",
      domain: "Work",
      requirement: "required",
      summary: "Obtain the authority decision for the actual activities and clients you perform.",
      why: "Calling work freelance or invoicing through a business does not by itself establish social-insurance status.",
      impact: "Anchors AHV contribution and insurance responsibilities",
      effort: "30–90 minutes",
      dueDate: today,
      dueLabel: "Confirm before relying on self-employed contribution assumptions",
      dateKind: "review",
      logic: `Self-employment is recorded, while official status is ${profile.selfEmployedStatusConfirmed ?? "unknown"}.`,
      documents: ["Client contracts and invoices", "Business expense evidence", "Compensation-office correspondence"],
      steps: [
        { id: "evidence", title: "Collect contracts, invoices and evidence of independent business risk" },
        { id: "office", title: "Submit or verify the status request with the compensation office" },
        { id: "decision", title: "Keep the written decision outside CH/OS and record the outcome here" },
      ],
      sources: [sources.selfEmployedContributions, sources.compensationOffices],
    }));
  }
  const selfEmployedProtectionDue = profile.employment === "self-employed"
    && (
      profile.selfEmployedAccidentCovered !== "yes"
      || profile.selfEmployedIncomeProtectionReviewed !== "yes"
      || !profile.businessContinuityReviewedDate
      || addYears(profile.businessContinuityReviewedDate, 1) <= today
    );
  if (selfEmployedProtectionDue) {
    result.push(rule("self-employed-protection-review", {
      title: "Map the income and cover gaps your business leaves with you",
      domain: "Protection",
      requirement: "recommended",
      summary: "Verify accident treatment and income benefits, illness income continuity, disability exposure and a short business-continuity plan.",
      why: "Self-employed people are not automatically placed in the same employee accident and salary-continuation structure.",
      impact: "Makes protection choices evidence-led rather than product-led",
      effort: "45–90 minutes",
      dueDate: addDays(today, 30),
      dueLabel: "Review within a month",
      dateKind: "review",
      logic: `Accident route: ${profile.selfEmployedAccidentCovered ?? "unknown"}; illness income review: ${profile.selfEmployedIncomeProtectionReviewed ?? "unknown"}; continuity review: ${profile.businessContinuityReviewedDate ?? "not recorded"}.`,
      documents: ["Current health and accident cover", "Essential monthly business and household costs", "Any daily-allowance or disability policies"],
      steps: [
        { id: "accident", title: "Confirm whether accident cover includes income benefits or only treatment costs" },
        { id: "illness", title: "Model how long business and household costs can run during illness" },
        { id: "disability", title: "Compare AHV/IV, pension and private disability evidence without assuming full income replacement" },
        { id: "continuity", title: "Write a minimal client, access and payment-continuity plan" },
        { id: "record", title: "Record the review outcomes and date in Profile" },
      ],
      sources: [sources.selfEmployedAccident, sources.selfEmployedContributions, sources.accidentHealth],
    }));
  }
  return result;
}

function taxAndPensionRules(profile: Profile, today: string): RuleDefinition[] {
  profile = employmentAwareProfile(profile);
  const result: RuleDefinition[] = [];
  const year = Number(today.slice(0, 4));
  if (profile.taxMode === "source" && profile.livesInSwitzerland !== "no") {
    const dueDate = `${year + 1}-03-31`;
    const incomeBandAtOrAboveThreshold = profile.incomeBand === "120-180"
      || profile.incomeBand === "180-plus";
    const mandatory = (profile.annualGrossIncome ?? 0) >= 120_000
      || incomeBandAtOrAboveThreshold
      || profile.otherUntaxedIncome === "yes"
      || profile.taxableWealthTrigger === "yes";
    result.push(rule(`tax-source-nov-${year}`, {
      title: mandatory ? `Prepare the mandatory ${year} ordinary assessment` : `Decide whether to request a ${year} ordinary assessment`,
      domain: "Tax",
      requirement: mandatory ? "required" : "recommended",
      summary: mandatory
        ? "Your answers indicate a federal trigger for a subsequent ordinary assessment."
        : "A voluntary request can unlock deductions, but moves you into the ordinary-assessment process.",
      why: "For Swiss residents taxed at source, the request deadline is 31 March of the following year and normally cannot be extended.",
      impact: mandatory ? "Tax compliance" : "Potential deductions with wider consequences",
      effort: "1–3 hours",
      dueDate,
      windowStart: `${year + 1}-01-01`,
      dueLabel: `For tax year ${year}: request or file by 31 March ${year + 1}`,
      dateKind: "deadline",
      logic: mandatory
        ? "Mandatory status is triggered by recorded income of at least CHF 120,000, other untaxed income, or a cantonal wealth trigger."
        : "No mandatory federal trigger is recorded. Canton-specific wealth thresholds still need verification.",
      calculation: profile.annualGrossIncome !== undefined
        ? `Recorded gross employment income: ${money(profile.annualGrossIncome)}; federal income trigger: CHF 120,000.`
        : incomeBandAtOrAboveThreshold
          ? `Your recorded income range starts at CHF 120,000, so the federal income trigger is treated as met. Add an exact amount only if you want a more precise explanation.`
          : "Exact gross employment income is not recorded.",
      documents: ["Salary certificate", "Bank and investment statements", "Pillar 2 and 3a confirmations", "Deduction evidence"],
      steps: [
        { id: "trigger", title: "Confirm income, other-income and wealth triggers with your canton" },
        { id: "model", title: "Model the full assessment before a voluntary request", optional: mandatory },
        { id: "documents", title: "Collect the tax-year documents" },
        { id: "request", title: `Submit the request or required filing by 31 March ${year + 1}` },
      ],
      sources: [sources.taxSource],
    }));
  }

  if (earnsFromWork(profile) && year === 2026) {
    const withoutPension = profile.pensionFundMember === "no";
    const needsIncome = withoutPension && profile.annualGrossIncome === undefined;
    if (profile.pensionFundMember === undefined || profile.pensionFundMember === "unknown") {
      result.push(rule("pillar3a-category-details", {
        title: "Confirm your Pillar 2 status before using a Pillar 3a limit",
        domain: "Pension",
        requirement: "recommended",
        summary: "CH/OS will not choose between the small and income-based Pillar 3a categories without this answer.",
        why: "Pillar 2 affiliation changes the contribution maximum. Showing a precise amount here would be misleading.",
        impact: "Prevents an incorrect contribution recommendation",
        effort: "1 minute",
        dueDate: today,
        dueLabel: "Confirm before planning a contribution",
        confidence: "needs-input",
        dateKind: "input",
        logic: "Your profile does not yet record whether you are affiliated with an occupational pension fund.",
        documents: ["Latest pension-fund statement or employment benefits summary"],
        steps: [{ id: "status", title: "Record your Pillar 2 affiliation in Profile" }],
        sources: [sources.pillar3a],
        conceptId: "pillar3a",
      }));
    } else if (needsIncome) {
      result.push(rule("pillar3a-income-details", {
        title: "Add eligible earned income before calculating Pillar 3a room",
        domain: "Pension",
        requirement: "recommended",
        summary: "Without Pillar 2, the maximum is income-based and cannot be calculated from an income range alone.",
        why: "The applicable maximum is 20% of eligible earned income up to the legal cap.",
        impact: "Prevents an incorrect contribution amount",
        effort: "2 minutes",
        dueDate: today,
        dueLabel: "Confirm before planning a contribution",
        confidence: "needs-input",
        dateKind: "input",
        logic: "Your profile records no Pillar 2 affiliation but does not contain an exact eligible-income amount.",
        documents: ["Current income estimate or latest self-employment accounts"],
        steps: [{ id: "income", title: "Record eligible annual earned income in Profile" }],
        sources: [sources.pillar3a],
        conceptId: "pillar3a",
      }));
    } else if (profile.pillar3aContributed === undefined) {
      result.push(rule("pillar3a-contribution-details", {
        title: `Record what you have already paid into Pillar 3a in ${year}`,
        domain: "Pension",
        requirement: "recommended",
        summary: "CH/OS needs the current-year total before it can calculate remaining contribution room.",
        why: "Assuming zero would turn an incomplete profile into an incorrect financial instruction.",
        impact: "Makes the year-end action precise",
        effort: "2 minutes",
        dueDate: today,
        dueLabel: "Confirm before planning another payment",
        confidence: "needs-input",
        dateKind: "input",
        logic: "No current-year Pillar 3a contribution total is recorded.",
        documents: ["Current-year Pillar 3a confirmations or account transactions"],
        steps: [{ id: "contributed", title: "Record the total credited so far this year" }],
        sources: [sources.pillar3a],
        conceptId: "pillar3a",
      }));
    } else {
      const max = withoutPension
        ? Math.min((profile.annualGrossIncome ?? 0) * 0.2, 36_288)
        : 7_258;
      const contributed = Math.max(0, profile.pillar3aContributed);
      const remaining = Math.max(0, max - contributed);
      if (max > 0 && remaining > 0) {
        result.push(rule(`pillar3a-${year}`, {
        title: `Decide whether to contribute the remaining ${money(remaining)} to Pillar 3a`,
        domain: "Pension",
        requirement: "optional",
        summary: `${money(contributed)} is recorded against an estimated ${money(max)} ${year} maximum.`,
        why: "Contribute only after checking liquidity, eligibility and tax value. CH/OS does not rank products or assume the maximum is right.",
        impact: "Retirement saving and possible tax deduction",
        effort: "30–60 minutes",
        dueDate: `${year}-12-15`,
        windowStart: `${year}-09-01`,
        dueLabel: `Internal planning date; eligible ${year} payments must arrive by year-end`,
        dateKind: "review",
        deadlineKind: "planning",
        hardDeadline: `${year}-12-31`,
        hardDeadlineLabel: `Eligible ${year} contribution must be credited by 31 December`,
        logic: withoutPension
          ? "Uses 20% of recorded eligible income, capped at CHF 36,288, for people without Pillar 2."
          : "Uses the 2026 small 3a maximum of CHF 7,258 for people affiliated with Pillar 2.",
        calculation: `${money(max)} maximum − ${money(contributed)} recorded = ${money(remaining)} remaining.`,
        documents: ["Current-year 3a confirmations", "Liquidity plan", "Tax estimate"],
        steps: [
          { id: "eligibility", title: "Confirm your 3a category and eligible earnings" },
          { id: "liquidity", title: `Confirm ${money(remaining)} is genuinely long-term money` },
          { id: "tax", title: "Estimate the tax effect without treating it as guaranteed" },
          { id: "pay", title: "If appropriate, transfer early enough to arrive before year-end" },
          { id: "receipt", title: "Save the contribution certificate" },
        ],
        sources: [sources.pillar3a],
        conceptId: "pillar3a",
        }));
      }
    }
  }

  if (year === 2026 && (profile.pillar3aContributed ?? 0) >= 7_258 && (profile.missed3aFor2025 ?? 0) > 0) {
    const catchup = Math.min(profile.missed3aFor2025 ?? 0, 7_258);
    result.push(rule("pillar3a-catchup-2025", {
      title: `Verify a possible ${money(catchup)} Pillar 3a catch-up for 2025`,
      domain: "Pension",
      requirement: "optional",
      summary: "2026 is the first year an eligible missed 2025 contribution can potentially be made retroactively.",
      why: "The 2026 ordinary maximum must be contributed first, and both years have eligibility conditions.",
      impact: "Potential additional retirement saving and deduction",
      effort: "30–60 minutes",
      dueDate: "2026-12-15",
      windowStart: "2026-09-01",
      dueLabel: "Verify with the 3a institution before year-end",
      dateKind: "review",
      deadlineKind: "planning",
      hardDeadline: "2026-12-31",
      hardDeadlineLabel: "Eligible catch-up contribution must be credited by 31 December 2026",
      logic: "Your profile records the 2026 ordinary maximum as funded and a missed 2025 amount.",
      calculation: `Recorded missed amount ${money(profile.missed3aFor2025 ?? 0)}, capped at CHF 7,258.`,
      documents: ["2025 and 2026 income evidence", "2026 3a confirmation", "Catch-up declaration"],
      steps: [
        { id: "eligibility", title: "Confirm eligible OASI income existed in 2025 and 2026" },
        { id: "ordinary", title: "Confirm the ordinary 2026 maximum has arrived" },
        { id: "institution", title: "Have the 3a institution validate the amount and process" },
        { id: "certificate", title: "Save the separate tax certificate" },
      ],
      sources: [sources.pillar3aCatchup],
    }));
  }

  const multipleEmployers = (profile.employerCount ?? 1) > 1;
  const detailedEmployers = profile.employers?.filter((employer) =>
    employer.annualGrossIncome !== undefined || Boolean(employer.label?.trim())) ?? [];
  const pensionThresholdIncome = multipleEmployers
    ? profile.highestAnnualIncomeFromOneEmployer
    : profile.annualGrossIncome;

  if (
    hasEmployeeContract(profile)
    && multipleEmployers
    && detailedEmployers.length === 0
    && (profile.annualGrossIncome ?? 0) >= 22_680
    && profile.highestAnnualIncomeFromOneEmployer === undefined
  ) {
    result.push(rule("pillar2-multi-employer-details", {
      title: "Add your highest annual salary from one employer",
      domain: "Pension",
      requirement: "recommended",
      summary: "Mandatory Pillar 2 coverage is assessed per employer, not from combined pay across several jobs.",
      why: "Using total income here could incorrectly label voluntary coverage as mandatory.",
      impact: "Unlocks the correct occupational-pension route",
      effort: "3 minutes",
      dueDate: addDays(today, 7),
      dueLabel: "Complete the work profile",
      confidence: "needs-input",
      dateKind: "input",
      logic: `${profile.employerCount} employers and combined income of ${money(profile.annualGrossIncome ?? 0)} are recorded, but the highest single-employer salary is unknown.`,
      documents: ["Annual salary or contract for each employer"],
      steps: [{ id: "profile", title: "Record the highest expected annual salary from any one employer" }],
      sources: [sources.pillar2],
    }));
  }

  if (
    hasEmployeeContract(profile)
    && detailedEmployers.length === 0
    && (pensionThresholdIncome ?? 0) >= 22_680
    && profile.pensionFundMember !== "yes"
  ) {
    result.push(rule("pillar2-coverage", {
      title: "Ask payroll to confirm mandatory Pillar 2 coverage",
      domain: "Pension",
      requirement: "required",
      summary: "Your recorded pay is at or above the 2026 BVG entry threshold, but fund membership is not confirmed.",
      why: "Some employment patterns and exceptions change coverage, so payroll or the fund should confirm it.",
      impact: "Checks retirement, disability and survivor cover",
      effort: "15 minutes",
      dueDate: addDays(today, 14),
      dueLabel: "Confirm within two weeks",
      dateKind: "review",
      logic: multipleEmployers
        ? `The highest recorded salary from one employer is ${money(pensionThresholdIncome ?? 0)}, at or above CHF 22,680; fund membership is ${profile.pensionFundMember ?? "unknown"}.`
        : `Recorded income ${money(pensionThresholdIncome ?? 0)} is at or above CHF 22,680; fund membership is ${profile.pensionFundMember ?? "unknown"}.`,
      documents: ["Employment contract", "Recent payslip", "Any pension certificate"],
      steps: [
        { id: "payroll", title: "Ask payroll which pension fund covers you and from what date" },
        { id: "exceptions", title: "Confirm any short-contract or multi-employer exception" },
        { id: "certificate", title: "Obtain the pension certificate and insured salary" },
      ],
      sources: [sources.pillar2],
    }));
  }

  for (const employer of detailedEmployers) {
    if ((employer.annualGrossIncome ?? 0) < 22_680 || employer.pensionFundMember === "yes") continue;
    const employerName = employer.label?.trim() || "this employer";
    result.push(rule(`pillar2-coverage-${employer.id}`, {
      title: `Confirm mandatory Pillar 2 coverage with ${employerName}`,
      domain: "Pension",
      requirement: "required",
      summary: `${money(employer.annualGrossIncome ?? 0)} annual pay is recorded for this employer, but its pension-fund membership is not confirmed.`,
      why: "The entry threshold is assessed separately for each employer.",
      impact: "Checks retirement, disability and survivor cover",
      effort: "15 minutes",
      dueDate: addDays(today, 14),
      dueLabel: "Confirm with this employer within two weeks",
      dateKind: "review",
      logic: `Employer record ${employerName}: ${money(employer.annualGrossIncome ?? 0)} is at or above CHF 22,680; fund status is ${employer.pensionFundMember ?? "unknown"}.`,
      documents: ["Employment contract", "Recent payslip", "Any pension certificate"],
      steps: [
        { id: "payroll", title: `Ask ${employerName} which pension fund covers you and from what date` },
        { id: "exceptions", title: "Confirm contract-duration and coordination exceptions" },
        { id: "certificate", title: "Obtain the pension certificate and insured salary" },
      ],
      sources: [sources.pillar2],
    }));
  }

  if (
    hasEmployeeContract(profile)
    && multipleEmployers
    && profile.highestAnnualIncomeFromOneEmployer !== undefined
    && profile.highestAnnualIncomeFromOneEmployer < 22_680
    && (profile.annualGrossIncome ?? 0) >= 22_680
  ) {
    result.push(rule("pillar2-voluntary-multi-employer", {
      title: "Evaluate voluntary Pillar 2 cover across your jobs",
      domain: "Pension",
      requirement: "optional",
      summary: "Your combined pay reaches the entry threshold, but no recorded employer does individually.",
      why: "That pattern can enable voluntary insurance; it does not make one employer's coverage automatically mandatory.",
      impact: "May consolidate retirement, disability and survivor cover",
      effort: "30–60 minutes",
      dueDate: addDays(today, 30),
      dueLabel: "Review with a fund or the Substitute Occupational Benefit Institution",
      dateKind: "review",
      logic: `Combined recorded income is ${money(profile.annualGrossIncome ?? 0)}, while the highest one-employer salary is ${money(profile.highestAnnualIncomeFromOneEmployer)}.`,
      documents: ["Contracts and annual salaries for every employer", "Any existing pension certificates"],
      steps: [
        { id: "list", title: "List annual pay and pension coverage separately for each employer" },
        { id: "fund", title: "Ask whether one employer's pension fund accepts voluntary combined cover" },
        { id: "substitute", title: "If needed, check the Substitute Occupational Benefit Institution route" },
        { id: "compare", title: "Review contributions and insured benefits before opting in" },
      ],
      sources: [sources.pillar2],
    }));
  }

  if ((profile.pillar2BuyInCapacity ?? 0) > 0) {
    result.push(rule("pillar2-buyin", {
      title: `Validate your ${money(profile.pillar2BuyInCapacity ?? 0)} Pillar 2 buy-in capacity`,
      domain: "Pension",
      requirement: "optional",
      summary: "A fund statement showing capacity is not itself a recommendation to contribute.",
      why: "Tax residence, liquidity, fund quality, earlier withdrawals and capital plans can change the decision.",
      impact: "Potential tax saving with long-term lock-up",
      effort: "1–2 hours",
      dueDate: addDays(today, 30),
      dueLabel: "Decision review; not a payment deadline",
      confidence: "review-window",
      dateKind: "review",
      logic: "The profile records positive capacity. CH/OS intentionally does not calculate a recommended contribution.",
      calculation: `Fund-reported capacity: ${money(profile.pillar2BuyInCapacity ?? 0)}.`,
      documents: ["Latest pension certificate", "Formal fund calculation", "Tax projection", "Liquidity and withdrawal plan"],
      steps: [
        { id: "fund", title: "Request a current formal calculation from the pension fund" },
        { id: "withdrawals", title: "Disclose earlier and planned capital withdrawals" },
        { id: "liquidity", title: "Stress-test liquidity after the funds become locked" },
        { id: "tax", title: "Verify deductibility and timing with the competent authority or adviser" },
        { id: "confirm", title: "Contribute only after the fund confirms the process", optional: true },
      ],
      sources: [sources.pillar2],
      conceptId: "pillar2-buyin",
    }));
  }

  if (profile.ahvGapsKnown === "yes") {
    result.push(rule("ahv-gap-followup", {
      title: "Resolve the AHV gaps shown on your account",
      domain: "Pension",
      requirement: "recommended",
      summary: "Your profile records a known contribution gap; unresolved gaps can reduce pension entitlement.",
      why: "The compensation office can explain whether a correction or limited back-payment route applies.",
      impact: "Protects first-pillar entitlement",
      effort: "30–90 minutes",
      dueDate: addDays(today, 21),
      dueLabel: "Contact the compensation office within three weeks",
      dateKind: "review",
      logic: "You explicitly recorded known gaps on the individual-account statement.",
      documents: ["Individual-account statement", "Salary evidence for missing periods", "Residence history"],
      steps: [
        { id: "mark", title: "Mark every missing or incorrect period" },
        { id: "evidence", title: "Collect payslips or salary certificates" },
        { id: "office", title: "Send a documented correction request" },
        { id: "result", title: "Request and check the updated statement" },
      ],
      sources: [sources.ahv],
      conceptId: "ahv-gap",
    }));
  } else if (!profile.ahvStatementDate) {
    result.push(rule("ahv-statement", {
      title: "Order your AHV individual-account statement",
      domain: "Pension",
      requirement: "recommended",
      summary: "The free statement shows contribution years and recorded income so you can spot gaps early.",
      why: "CH/OS cannot infer AHV postings from employment history.",
      impact: "Verifies first-pillar history",
      effort: "15 minutes",
      dueDate: addDays(today, 30),
      dueLabel: "One-time baseline within a month",
      dateKind: "review",
      logic: "No AHV statement date is recorded in your profile.",
      documents: ["OASI number for the official request only", "Employment history for comparison"],
      steps: [
        { id: "order", title: "Order the free statement from the official service" },
        { id: "compare", title: "Compare every contribution year with your history" },
        { id: "profile", title: "Record the date and whether gaps were found in CH/OS" },
      ],
      sources: [sources.ahv],
      conceptId: "ahv-gap",
    }));
  } else if (addYears(profile.ahvStatementDate, 3) <= today) {
    result.push(rule("ahv-statement-refresh", {
      title: "Refresh your AHV individual-account statement",
      domain: "Pension",
      requirement: "recommended",
      summary: `The last statement in your profile is dated ${profile.ahvStatementDate}. A fresh copy can reveal later posting gaps or corrections.`,
      why: "A statement is evidence at a point in time; it does not stay current as new contribution years are posted.",
      impact: "Keeps first-pillar evidence current",
      effort: "15–30 minutes",
      dueDate: addDays(today, 30),
      dueLabel: "Refresh within a month",
      dateKind: "review",
      logic: "The recorded AHV statement is at least three years old.",
      documents: ["Latest individual-account statement", "Employment history since that statement"],
      steps: [
        { id: "order", title: "Order a new statement from the official service" },
        { id: "compare", title: "Compare new contribution years and any corrections" },
        { id: "update", title: "Update the statement date and gap status in CH/OS" },
      ],
      sources: [sources.ahv],
      conceptId: "ahv-gap",
    }));
  }

  if (
    earnsFromWork(profile)
    && profile.pensionFundMember === "yes"
    && profile.pillar2StatementDate
    && addYears(profile.pillar2StatementDate, 2) <= today
  ) {
    result.push(rule("pillar2-statement-refresh", {
      title: "Request a current Pillar 2 pension certificate",
      domain: "Pension",
      requirement: "recommended",
      summary: `Your latest recorded certificate is dated ${profile.pillar2StatementDate}; salary, benefits and buy-in capacity may now differ.`,
      why: "Pillar 2 decisions should use a current fund certificate, especially after salary or job changes.",
      impact: "Updates retirement, disability and survivor-cover evidence",
      effort: "15–30 minutes",
      dueDate: addDays(today, 30),
      dueLabel: "Refresh before making pension decisions",
      dateKind: "review",
      logic: "The recorded Pillar 2 statement is at least two years old.",
      documents: ["Latest pension certificate", "Current payslip"],
      steps: [
        { id: "request", title: "Request the latest certificate from the pension fund or payroll" },
        { id: "compare", title: "Compare insured salary, projected benefits and beneficiaries" },
        { id: "update", title: "Record the new statement date and any buy-in capacity" },
      ],
      sources: [sources.pillar2],
    }));
  }
  return result;
}

function healthRules(profile: Profile): RuleDefinition[] {
  if (
    profile.livesInSwitzerland === "no"
    || profile.permitType === "G"
    || profile.residency === "swiss"
    || profile.healthInsuranceConfirmed !== "no"
    || !profile.residenceStartDate
  ) return [];
  const dueDate = addDays(profile.residenceStartDate, 92);
  return [rule("health-enrollment", {
    title: "Complete compulsory Swiss health-insurance enrollment",
    domain: "Health",
    requirement: "required",
    summary: `Your residence start points to a three-month enrollment deadline around ${dueDate}.`,
    why: "People taking up residence or work in Switzerland generally have three months to arrange compulsory basic insurance.",
    impact: "Legal compliance and healthcare cover",
    effort: "1–2 hours",
    dueDate,
    windowStart: profile.residenceStartDate,
    dueLabel: "Within three months of taking up residence or work; verify the exact start",
    confidence: "forecast",
    dateKind: "forecast",
    logic: "The profile records a foreign resident, no confirmed basic insurance, and a residence start date.",
    calculation: `${profile.residenceStartDate} + approximately 3 months = ${dueDate}.`,
    documents: ["Residence registration", "Permit evidence", "Household details"],
    steps: [
      { id: "compare", title: "Compare basic premiums on official Priminfo" },
      { id: "choose", title: "Choose insurer, model and deductible" },
      { id: "enrol", title: "Enroll from the correct residence or work start date" },
      { id: "commune", title: "Provide proof to the commune if requested" },
    ],
    sources: [sources.healthEnrollment],
  })];
}

function homeRules(profile: Profile, today: string): RuleDefinition[] {
  if (profile.livesInSwitzerland === "no" || profile.housing !== "renter") return [];
  const currentRate = 1.25;
  if (profile.leaseReferenceRate === undefined) {
    return [rule("rent-rate-basis", {
      title: "Find the reference rate behind your current rent",
      domain: "Home",
      requirement: "recommended",
      summary: "The lease or latest rent-adjustment notice normally records the mortgage reference rate used for your net rent.",
      why: "The federal rate is 1.25% as reviewed on 27 July 2026, but CH/OS needs your contract basis before suggesting a reduction check.",
      impact: "Unlocks a contract-specific rent review",
      effort: "10 minutes",
      dueDate: "2026-08-25",
      dueLabel: "Before the next federal publication on 1 September 2026",
      confidence: "needs-input",
      dateKind: "input",
      logic: "You rent, but the reference rate underlying the current net rent is not recorded.",
      calculation: "Current federal reference rate: 1.25%. Your contractual comparison rate is still unknown.",
      documents: ["Lease", "Latest rent-adjustment notice"],
      steps: [
        { id: "lease", title: "Check the lease for its reference-rate basis" },
        { id: "notice", title: "If rent changed later, use the rate in the latest valid adjustment notice" },
        { id: "profile", title: "Record that rate in CH/OS" },
      ],
      sources: [sources.rent],
    })];
  }
  if (profile.leaseReferenceRate > currentRate) {
    return [rule("rent-reduction-check", {
      title: `Check a possible rent reduction from ${profile.leaseReferenceRate}% to 1.25%`,
      domain: "Home",
      requirement: "recommended",
      summary: "Your recorded contract basis is above the current federal reference rate.",
      why: "A lower reference rate can create a reduction claim, but inflation, operating costs and the individual lease can offset it.",
      impact: "Potential recurring rent reduction",
      effort: "30–60 minutes",
      dueDate: addDays(today, 14),
      dueLabel: "Review the individual lease before writing to the landlord",
      dateKind: "review",
      logic: "The recorded lease basis exceeds the 1.25% federal reference rate in force since September 2025.",
      calculation: `Recorded lease basis ${profile.leaseReferenceRate}% − current 1.25% = ${(profile.leaseReferenceRate - currentRate).toFixed(2)} percentage points. This is not the final rent reduction.`,
      documents: ["Lease", "Latest rent-adjustment notice", "Net-rent history"],
      steps: [
        { id: "basis", title: "Confirm the recorded reference rate and current net rent" },
        { id: "offsets", title: "Account for inflation and documented operating-cost changes" },
        { id: "calculate", title: "Use a trusted tenant calculator or obtain advice for the individual lease" },
        { id: "request", title: "If supported, send a documented reduction request to the landlord" },
        { id: "reply", title: "Track the landlord’s 30-day response window" },
      ],
      sources: [sources.rent],
    })];
  }
  return [rule("rent-rate-next-publication", {
    title: "Check the September mortgage reference-rate publication",
    domain: "Home",
    requirement: "optional",
    summary: "Your recorded lease basis is not above the current 1.25% rate, so there is no rate-only reduction signal today.",
    why: "The Federal Housing Office publishes the next update on 1 September 2026.",
    impact: "Keeps the rent basis current",
    effort: "5 minutes",
    dueDate: "2026-09-02",
    dueLabel: "After the federal publication on 1 September 2026",
    dateKind: "review",
    logic: "The recorded lease basis is at or below the current federal rate.",
    calculation: `Recorded lease basis: ${profile.leaseReferenceRate}%; current federal rate: 1.25%.`,
    documents: ["Lease or latest rent notice"],
    steps: [
      { id: "rate", title: "Check the newly published federal reference rate" },
      { id: "compare", title: "Compare it with the current lease basis" },
      { id: "profile", title: "Update CH/OS only if your contractual basis changes" },
    ],
    sources: [sources.rent],
  })];
}

function moneyRules(profile: Profile, today: string): RuleDefinition[] {
  const result: RuleDefinition[] = [];

  if (profile.cashBufferMonths === undefined) {
    result.push(rule("cash-buffer-baseline", {
      title: "Set a cash-resilience baseline",
      domain: "Money",
      requirement: "optional",
      summary: "Estimate how many months of essential spending are accessible without selling long-term investments.",
      why: "A rough runway turns generic saving advice into a plan that respects taxes, insurance bills and known life changes.",
      impact: "Unlocks a tailored resilience plan",
      effort: "10 minutes",
      dueDate: addDays(today, 21),
      dueLabel: "Optional profile baseline",
      confidence: "needs-input",
      dateKind: "input",
      logic: "No cash-buffer estimate is recorded. CH/OS asks for months, not an account balance.",
      documents: ["Essential monthly-spend estimate", "Known large bills"],
      steps: [
        { id: "spend", title: "Estimate essential monthly spending" },
        { id: "months", title: "Record the number of accessible months in Profile" },
      ],
      sources: [],
    }));
  } else if (profile.cashBufferMonths < 3) {
    result.push(rule("cash-buffer-rebuild", {
      title: "Build your next cash-buffer milestone",
      domain: "Money",
      requirement: "recommended",
      summary: `You estimate ${profile.cashBufferMonths} month${profile.cashBufferMonths === 1 ? "" : "s"} of accessible cash. Create a staged plan before locking more money away.`,
      why: "The right target is personal, but a visible first milestone reduces the risk that a tax bill, deductible or job change forces expensive decisions.",
      impact: "Improves short-term resilience",
      effort: "30 minutes, then automatic transfers",
      dueDate: addDays(today, 14),
      dueLabel: "Set the next achievable milestone within two weeks",
      dateKind: "review",
      logic: "The recorded cash runway is below three months. Three months is used as a planning signal, not a universal rule.",
      documents: ["Essential monthly-spend estimate", "Tax reserve", "Known annual bills"],
      steps: [
        { id: "separate", title: "Separate true emergency cash from tax and planned-spending reserves" },
        { id: "first", title: "Choose the next achievable milestone, starting with one full month if needed" },
        { id: "automate", title: "Set a recurring transfer that fits your actual budget" },
        { id: "pause", title: "Recheck optional locked savings until the buffer is stable", optional: true },
      ],
      sources: [],
    }));
  } else {
    result.push(rule("cash-buffer-review", {
      title: `Stress-test your ${profile.cashBufferMonths}-month cash buffer`,
      domain: "Money",
      requirement: "optional",
      summary: "Check that the estimate excludes invested assets and still covers taxes, deductibles and known near-term costs.",
      why: "A headline number can look healthy while money is already committed elsewhere.",
      impact: "Keeps liquidity intentional",
      effort: "20 minutes",
      dueDate: addDays(today, 90),
      dueLabel: "Quarterly resilience review",
      dateKind: "review",
      logic: `The profile records ${profile.cashBufferMonths} months of accessible cash.`,
      documents: ["Core spending estimate", "Tax reserve", "Known large expenses"],
      steps: [
        { id: "access", title: "Confirm the funds are accessible without selling long-term investments" },
        { id: "reserved", title: "Subtract taxes and known bills already assigned to that cash" },
        { id: "target", title: "Adjust your personal target after upcoming life changes" },
      ],
      sources: [],
    }));
  }

  const baselineMissing = profile.monthlyNetIncome === undefined
    || profile.monthlyTotalExpenses === undefined
    || profile.monthlyEssentialExpenses === undefined
    || profile.liquidSavings === undefined
    || !profile.investmentHorizon
    || !profile.riskComfort;
  if (baselineMissing) {
    result.push(rule("finance-baseline", {
      title: "Build your personal money baseline",
      domain: "Money",
      requirement: "optional",
      summary: "Add a few monthly estimates, your investment horizon and how you react to losses so CH/OS can separate resilience from long-term investing.",
      why: "A broker or product recommendation without cash flow, purpose and time horizon is not a financial plan.",
      impact: "Unlocks a neutral, situation-aware wealth plan",
      effort: "10–15 minutes",
      dueDate: addDays(today, 21),
      dueLabel: "Optional planning baseline",
      confidence: "needs-input",
      dateKind: "input",
      logic: "At least one of monthly income, spending, accessible cash, investment horizon or loss comfort is missing.",
      documents: ["A normal month of income and spending", "Accessible savings estimate", "Known goals and dates"],
      steps: [
        { id: "cashflow", title: "Estimate monthly net income, essential spending and total spending", conceptId: "savings-rate" },
        { id: "liquidity", title: "Record accessible savings excluding investments and assigned reserves", conceptId: "cash-buffer" },
        { id: "horizon", title: "Choose when the invested money may first be needed", conceptId: "investment-horizon" },
        { id: "risk", title: "Record your comfort and likely reaction during a major market fall", conceptId: "risk-capacity" },
      ],
      sources: [sources.investorProtection],
    }));
  }

  const financiallyReady = profile.consumerDebt !== "yes"
    && (profile.cashBufferMonths ?? 0) >= 3
    && Boolean(profile.investmentHorizon)
    && profile.investmentHorizon !== "under-3";
  if (financiallyReady && profile.brokerStatus === "none") {
    result.push(rule("broker-due-diligence", {
      title: "Compare a broker on more than trading fees",
      domain: "Money",
      requirement: "optional",
      summary: "Compare regulation, custody, currency conversion, statements, total fees and account complexity before opening an investment account.",
      why: "IBKR can be a useful low-cost commercial example for some Swiss residents, but the best account depends on service needs, tax records, products and tolerance for complexity.",
      impact: "Avoids a cheap account becoming an expensive operational mismatch",
      effort: "45–75 minutes",
      dueDate: addDays(today, 30),
      dueLabel: "Before opening or transferring a brokerage account",
      dateKind: "review",
      logic: "The profile records at least three months of cash resilience, no expensive-debt signal, a longer horizon and no current broker.",
      documents: ["Fee schedules", "Custody and regulator details", "Sample Swiss tax statement", "Currency-conversion terms"],
      steps: [
        { id: "needs", title: "Write down the markets, service and reporting features you actually need" },
        { id: "entity", title: "Verify the legal entity, regulator and asset-custody arrangement" },
        { id: "cost", title: "Compare trading, custody, foreign-exchange and product costs together" },
        { id: "tax", title: "Confirm annual statements contain the data needed for your Swiss tax return" },
        { id: "safety", title: "Disable margin, options and complex permissions unless deliberately required", optional: true },
      ],
      sources: [sources.investorProtection],
      conceptId: "broker-custody",
    }));
  }

  if (!baselineMissing) {
    result.push(annualRule("investment-policy-review", 10, 15, {
      title: "Review your written investment strategy",
      domain: "Money",
      requirement: "recommended",
      summary: "Check goals, time horizon, diversification, fees, contribution rhythm and rebalancing rules without reacting to short-term headlines.",
      why: "A written policy turns investing into a repeatable process and makes product changes answerable to a goal.",
      impact: "Keeps risk, cost and behaviour aligned",
      effort: "45–60 minutes",
      dueLabel: "Annual strategy review in October",
      windowStartMonth: 9,
      dateKind: "review",
      logic: `Investment horizon: ${profile.investmentHorizon}; risk comfort: ${profile.riskComfort}; last policy review: ${profile.investmentPolicyReviewedDate ?? "not recorded"}.`,
      documents: ["Goal and horizon", "Current allocation", "Total fees", "Contribution and rebalancing rules"],
      steps: [
        { id: "goal", title: "Confirm the purpose and earliest date each investment pot may be needed", conceptId: "investment-horizon" },
        { id: "risk", title: "Stress-test whether the current mix could be held through a severe fall", conceptId: "risk-capacity" },
        { id: "diversification", title: "Check global, currency and single-company concentration" },
        { id: "fees", title: "Calculate broker, fund, currency and advice costs together" },
        { id: "rebalance", title: "Rebalance only if the written range requires it", conceptId: "rebalancing" },
        { id: "record", title: "Record the completed review date in Profile" },
      ],
      sources: [sources.investorProtection, sources.professionalTrading],
    }));
  }

  const eligibleFor3a = profile.employment === "employed"
    || profile.employment === "self-employed"
    || profile.hasEmploymentContract === "yes";
  if (eligibleFor3a) {
    result.push(annualRule("pillar3a-strategy-review", 11, 15, {
      title: "Review how your Pillar 3a is implemented",
      domain: "Pension",
      requirement: "recommended",
      summary: "Confirm the contribution plan, liquidity, investment allocation, fees and provider structure before the year-end rush.",
      why: "Cash 3a, invested 3a and insurance-linked 3a solve different needs. Finpension and VIAC are commercial examples to compare, not automatic winners.",
      impact: "Aligns tax saving, cost, risk and withdrawal flexibility",
      effort: "30–60 minutes",
      dueLabel: "Review before the year-end contribution window",
      windowStartMonth: 9,
      dateKind: "review",
      hardDeadlineMonth: 12,
      hardDeadlineDay: 31,
      hardDeadlineLabel: "The contribution must be credited within the calendar year",
      logic: `3a type: ${profile.thirdPillarType ?? "not recorded"}; provider category: ${profile.thirdPillarProvider ?? "not recorded"}.`,
      documents: ["Current 3a statement", "Fee schedule", "Investment allocation", "Beneficiary and withdrawal terms"],
      steps: [
        { id: "liquidity", title: "Confirm the amount can remain restricted until a permitted withdrawal" },
        { id: "type", title: "Decide whether cash, invested or insurance-linked 3a fits the actual need" },
        { id: "cost", title: "Compare all-in fees and long-term allocation, not only recent returns" },
        { id: "providers", title: "Compare at least two suitable providers on the same assumptions" },
        { id: "payment", title: "Schedule the contribution early enough to be credited by year-end" },
      ],
      sources: [sources.pillar3a],
      conceptId: "pillar3a",
    }));
  }

  if (profile.usesUSListedSecurities === "yes") {
    const signedYear = profile.w8BenSignedDate ? Number(profile.w8BenSignedDate.slice(0, 4)) : undefined;
    const validThroughYear = signedYear ? signedYear + 3 : undefined;
    if (!validThroughYear || Number(today.slice(0, 4)) >= validThroughYear) {
      const expiry = validThroughYear ? `${validThroughYear}-12-31` : undefined;
      const dueDate = !expiry || expiry < today ? addDays(today, 14) : `${validThroughYear}-12-15`;
      result.push(rule("w8ben-review", {
        title: "Confirm your W-8BEN status with the broker",
        domain: "Tax",
        requirement: "recommended",
        summary: "Verify that the broker or US payer has a valid foreign-status form for the correct account holder.",
        why: "A valid W-8BEN can allow treaty withholding on eligible US income. It is not the Swiss DA-1 reclaim and US persons generally use a different form.",
        impact: "Can prevent avoidable excess US dividend withholding",
        effort: "10–20 minutes",
        dueDate,
        dueLabel: expiry ? "Review before the general form-validity end" : "Confirm the current form within two weeks",
        dateKind: "review",
        hardDeadline: expiry,
        hardDeadlineLabel: expiry ? "General validity ends, unless circumstances changed earlier" : undefined,
        logic: profile.w8BenSignedDate
          ? `The recorded signing date is ${profile.w8BenSignedDate}; general validity would run through 31 December ${validThroughYear}, unless circumstances changed.`
          : "US-listed securities are recorded but no W-8BEN signing date is recorded.",
        calculation: signedYear
          ? `Signing year ${signedYear} + three succeeding calendar years = general validity through 31 December ${validThroughYear}.`
          : undefined,
        documents: ["Broker tax-form status", "Account-holder tax residence", "W-8BEN confirmation"],
        steps: [
          { id: "status", title: "Open the broker’s tax-form status for the correct account holder", conceptId: "w8ben" },
          { id: "person", title: "Confirm W-8BEN is appropriate; US persons should verify the correct alternative" },
          { id: "submit", title: "Submit or renew through the broker or payer—not through CH/OS" },
          { id: "date", title: "Record the signing date in Profile" },
        ],
        sources: [sources.usaTaxForms, sources.w8ben],
        conceptId: "w8ben",
      }));
    }

    result.push(annualRule("foreign-dividend-tax-pack", 3, 1, {
      title: "Prepare foreign-dividend evidence for DA-1",
      domain: "Tax",
      requirement: "recommended",
      summary: "Reconcile gross foreign dividends, withholding and holdings before completing the cantonal tax return and any DA-1 request.",
      why: "DA-1 can credit eligible non-refundable foreign withholding, but the underlying income and assets must be declared and the cantonal process controls filing.",
      impact: "Can recover eligible foreign withholding without mixing up W-8BEN and DA-1",
      effort: "30–60 minutes",
      dueLabel: "Prepare with the cantonal tax return; verify the cantonal deadline",
      windowStartMonth: 1,
      dateKind: "review",
      pastDueBehavior: "verify-now",
      logic: `US-listed securities are recorded. Last DA-1 tax year: ${profile.da1LastFiledTaxYear ?? "not recorded"}.`,
      documents: ["Annual broker tax statement", "Gross dividends and foreign withholding", "Year-end holdings", "Prior DA-1 decision if any"],
      steps: [
        { id: "statement", title: "Download the annual broker tax statement" },
        { id: "reconcile", title: "Reconcile gross income, country and withholding by position" },
        { id: "declare", title: "Declare the foreign assets and income in the cantonal return" },
        { id: "da1", title: "Complete DA-1 through the canton’s current filing route if eligible", conceptId: "da1" },
        { id: "rus", title: "Check R-US 164 if additional US tax was retained by a Swiss qualified intermediary", optional: true },
      ],
      sources: [sources.da1, sources.usaTaxForms],
      conceptId: "da1",
    }));
  }

  if (profile.usesMarginOrFrequentTrading === "yes") {
    result.push(rule("professional-securities-trading-review", {
      title: "Check professional securities-trading risk",
      domain: "Tax",
      requirement: "recommended",
      summary: "Review turnover, holding periods, leverage, derivatives and reliance on trading income against the FTA framework.",
      why: "Private gains are generally tax-free, but the classification is fact-specific and the safe-harbour conditions are cumulative.",
      impact: "Reduces the risk of an unexpected taxable-income classification",
      effort: "45–90 minutes",
      dueDate: addDays(today, 14),
      dueLabel: "Before increasing frequent, leveraged or derivatives trading",
      dateKind: "review",
      logic: "The profile records margin use or frequent trading. CH/OS cannot determine tax status from that answer alone.",
      documents: ["Complete trade history", "Holding periods and turnover", "Financing and margin statements", "Derivative purpose"],
      steps: [
        { id: "criteria", title: "Read the FTA safe-harbour criteria in Circular 36" },
        { id: "metrics", title: "Calculate turnover, typical holding period and leverage" },
        { id: "records", title: "Preserve trade, financing and strategy records" },
        { id: "advice", title: "Obtain qualified tax advice for a material or ambiguous case" },
      ],
      sources: [sources.professionalTrading],
      conceptId: "professional-securities-trading",
    }));
  }

  return result;
}

function lifecycleRules(profile: Profile, today: string): RuleDefinition[] {
  profile = employmentAwareProfile(profile);
  const result: RuleDefinition[] = [];
  if (profile.nextEvent === "moving" && !profile.eventDate) {
    result.push(rule("move-date-details", {
      title: "Add the move date when it is confirmed",
      domain: "Home",
      requirement: "recommended",
      summary: "Your moving focus is saved. CH/OS will wait for a real date before sequencing registration and handover actions.",
      why: "Move administration is date-sensitive, so an invented date would create false urgency.",
      impact: "Unlocks the dated move checklist",
      effort: "2 minutes",
      dueDate: addDays(today, 7),
      dueLabel: profile.eventDateCertainty === "ongoing" ? "Review as the move becomes definite" : "Add the date when known",
      confidence: "needs-input",
      dateKind: "input",
      logic: "Moving home is selected, but no confirmed or approximate date is recorded.",
      documents: ["Lease, purchase agreement or handover confirmation if available"],
      steps: [{ id: "date", title: "Record the confirmed or approximate move date in Profile" }],
      sources: [sources.moving],
    }));
  }
  if (profile.employment === "retired") {
    result.push(rule("retirement-operating-check", {
      title: "Run your retirement income and administration check",
      domain: "Pension",
      requirement: "recommended",
      summary: "Bring AHV, occupational pensions, taxes, health cover and beneficiary details into one current view.",
      why: "Retirement changes the operating rhythm of several systems at once; it should not be modelled as unemployment.",
      impact: "Reduces income and administration surprises",
      effort: "60–90 minutes",
      dueDate: addDays(today, 30),
      dueLabel: "Complete a retirement baseline within a month",
      dateKind: "review",
      logic: "Your primary life stage is recorded as retired. CH/OS intentionally suppresses job-search and RAV actions.",
      documents: ["AHV decision", "Pension statements", "Tax estimate", "Health and accident cover"],
      steps: [
        { id: "income", title: "Confirm AHV and Pillar 2 payment or withdrawal arrangements" },
        { id: "tax", title: "Update the household tax and cash-flow estimate" },
        { id: "cover", title: "Confirm health and accident cover after employment" },
        { id: "beneficiaries", title: "Review pension beneficiaries and essential legal documents" },
        { id: "benefits", title: "Check supplementary-benefit eligibility if income does not cover minimum living costs", optional: true },
      ],
      sources: [sources.retirement, sources.retirementForms],
    }));
  }

  const age = profile.birthYear ? Number(today.slice(0, 4)) - profile.birthYear : undefined;
  const nonEmployedStage = profile.employment === "student"
    || profile.employment === "caregiver"
    || profile.employment === "inactive";
  if (nonEmployedStage && !hasEmployeeContract(profile) && (age === undefined || age >= 21)) {
    result.push(rule("ahv-non-employed-check", {
      title: "Confirm your AHV contribution route while not employed",
      domain: "Pension",
      requirement: "recommended",
      summary: profile.employment === "student"
        ? "Students may have a specific annual AHV contribution route; paid work and age can change the result."
        : "People without gainful employment may still owe AHV contributions, with spouse and contribution exceptions.",
      why: "Unrecorded contribution years can reduce later benefits, and the compensation office decides individual cases.",
      impact: "Helps prevent a first-pillar contribution gap",
      effort: "20–45 minutes",
      dueDate: addDays(today, 30),
      dueLabel: "Verify with the compensation office within a month",
      dateKind: "review",
      logic: `Your primary status is “${profile.employment}” and no employment contract is recorded.`,
      documents: ["Residence and marital status", "Any earned income and contribution evidence", "Spouse contribution evidence if relevant"],
      steps: [
        { id: "route", title: "Read the official non-employed or student contribution guidance" },
        { id: "office", title: "Confirm your status with the competent compensation office" },
        { id: "evidence", title: "Keep the assessment or contribution evidence" },
        { id: "statement", title: "Later verify the year on your AHV account statement" },
      ],
      sources: [sources.nonEmployedAhv, sources.ahv],
    }));
  }

  if (profile.receivesIvOrEl === "yes") {
    result.push(rule("iv-social-insurance-coordination", {
      title: "Verify how work, IV/EL and AHV contributions fit together",
      domain: "Pension",
      requirement: "recommended",
      summary: "Partial work and disability-related benefits can change contribution status, pension coordination and reporting duties.",
      why: "CH/OS does not infer entitlement or perform the official comparative calculation from income alone.",
      impact: "Reduces benefit, contribution and pension surprises",
      effort: "45–90 minutes",
      dueDate: addDays(today, 30),
      dueLabel: "Confirm the current arrangement within a month",
      dateKind: "review",
      logic: "IV or supplementary-benefit context is recorded. This creates a verification workflow, not an eligibility calculation.",
      documents: ["Latest IV/EL decision", "Employment contract and payslips", "Pension-fund certificate", "AHV contribution decision"],
      steps: [
        { id: "decision", title: "List the current benefit decisions and their reporting contacts" },
        { id: "work", title: "Confirm work percentage, income and any change-reporting duty" },
        { id: "ahv", title: "Ask the compensation office whether a non-employed contribution assessment is needed" },
        { id: "pension", title: "Confirm occupational-pension disability and contribution coordination" },
        { id: "evidence", title: "Keep written decisions and update CH/OS dates, not documents" },
      ],
      sources: [sources.nonEmployedAhv, sources.ahv, sources.pillar2],
    }));
  }

  if (profile.nextEvent === "family") {
    if (!profile.familyEventType || !profile.eventDate) {
      result.push(rule("family-event-details", {
        title: "Define the family change so CH/OS can build the right checklist",
        domain: "Protection",
        requirement: "recommended",
        summary: "Birth, adoption, separation and shared-parenting changes have different documents and authority paths.",
        why: "A generic family event would hide the actions that actually matter.",
        impact: "Unlocks a dated family plan",
        effort: "2 minutes",
        dueDate: addDays(today, 7),
        dueLabel: "Complete the family-event details",
        confidence: "needs-input",
        dateKind: "input",
        logic: "Family change is selected, but its type or date is missing.",
        documents: [],
        steps: [{ id: "profile", title: "Choose the family event type and approximate date in Profile" }],
        sources: [sources.birthRegistration, sources.adoptionLeave, sources.parentalResponsibility],
      }));
    } else if (profile.familyEventType === "birth") {
      const prepDate = addDays(profile.eventDate, -30) < today ? today : addDays(profile.eventDate, -30);
      result.push(rule("family-birth-plan", {
        title: "Prepare the birth and first-month administration",
        domain: "Protection",
        requirement: "recommended",
        summary: `Use ${profile.eventDate} as the planning anchor for employer, civil-status, insurance and allowance actions.`,
        why: "The exact leave, allowance and insurance routes depend on both parents and the canton, so CH/OS prepares evidence rather than calculating entitlement.",
        impact: "Reduces a high-pressure administration backlog",
        effort: "60–90 minutes",
        dueDate: prepDate,
        dueLabel: "Prepare before the expected birth where possible",
        dateKind: "review",
        logic: "A birth is recorded as the next family event.",
        documents: ["Employer leave policies", "Civil-status documents requested by the hospital", "Insurance contacts", "Childcare application dates"],
        steps: [
          { id: "employers", title: "Confirm leave and claim routes separately with each parent's employer" },
          { id: "civil", title: "Ask the hospital which civil-status documents it needs" },
          { id: "insurance", title: "Decide the child's basic-insurance route and effective date" },
          { id: "allowance", title: "Identify which employer or compensation office will receive the family-allowance claim" },
          { id: "childcare", title: "Record any childcare or municipal application dates", optional: true },
        ],
        sources: [sources.pregnancyWork, sources.birthRegistration],
      }));
      result.push(rule("family-birth-registration", {
        title: "Confirm the birth was registered",
        domain: "Residency",
        requirement: "required",
        summary: "A birth in Switzerland must be registered within three days; the hospital or attending medical staff normally does this.",
        why: "The parent needs to act directly when no medical professional handled the registration or if the hospital asks for missing evidence.",
        impact: "Completes the child's civil registration",
        effort: "5–45 minutes",
        dueDate: addDays(profile.eventDate, 3),
        windowStart: profile.eventDate,
        dueLabel: "Within three days of birth; normally handled by the hospital",
        dateKind: "deadline",
        logic: "The expected birth date is used as a planning anchor; replace it if the actual date differs.",
        documents: ["Parents' identity and requested civil-status documents", "Hospital confirmation"],
        steps: [
          { id: "hospital", title: "Confirm who is submitting the birth registration" },
          { id: "documents", title: "Provide any civil-status documents requested" },
          { id: "confirm", title: "Save confirmation or update the actual birth date" },
        ],
        sources: [sources.birthRegistration],
      }));
    } else if (profile.familyEventType === "adoption") {
      result.push(rule("family-adoption-plan", {
        title: "Prepare the adoption, leave and allowance file",
        domain: "Protection",
        requirement: "recommended",
        summary: `Use ${profile.eventDate} as the expected placement or adoption date and verify eligibility with the responsible offices.`,
        why: "Adoption allowance is not automatic and eligible leave must be used within the year after adoption.",
        impact: "Protects time-off and claim administration",
        effort: "60–90 minutes",
        dueDate: addDays(profile.eventDate, -30) < today ? today : addDays(profile.eventDate, -30),
        dueLabel: "Prepare before placement or adoption where possible",
        dateKind: "review",
        logic: "Adoption is recorded as the next family event.",
        documents: ["Adoption or placement decision", "Employment and AHV contribution evidence", "Leave plan"],
        steps: [
          { id: "eligibility", title: "Check the official age, AHV and employment conditions" },
          { id: "leave", title: "Agree how eligible leave will be shared and recorded" },
          { id: "claim", title: "Submit the adoption-allowance request to the federal compensation office" },
          { id: "insurance", title: "Confirm the child's health-insurance and family-allowance routes" },
        ],
        sources: [sources.adoptionLeave],
      }));
    } else {
      result.push(rule("family-separation-coordination", {
        title: "Create a private coordination record for the family change",
        domain: "Protection",
        requirement: "recommended",
        summary: "Track agreements, payment cadence, child-related contacts and review dates without storing legal documents in CH/OS.",
        why: "Parental responsibility, custody and support arrangements are distinct and case-specific.",
        impact: "Reduces missed handovers, payments and administrative updates",
        effort: "45–90 minutes",
        dueDate: profile.eventDate < today ? today : profile.eventDate,
        dueLabel: "At the effective date of the arrangement",
        dateKind: "review",
        logic: `The recorded family change is ${profile.familyEventType}. CH/OS does not calculate rights or maintenance.`,
        documents: ["Agreement or order kept outside CH/OS", "Insurance and allowance contacts", "Address, school and medical contacts"],
        steps: [
          { id: "dates", title: "Record the agreement date, payment cadence and next review date as personal actions" },
          { id: "contacts", title: "Confirm address, school, medical and emergency-contact updates" },
          { id: "insurance", title: "Confirm child-related insurance and family-allowance ownership" },
          { id: "tax", title: "Ask the canton how the arrangement affects each person's tax evidence" },
          { id: "legal", title: "Use qualified local advice for rights, custody or support questions", optional: true },
        ],
        sources: [sources.parentalResponsibility],
      }));
    }
  }

  if (profile.nextEvent === "leaving") {
    if (!profile.eventDate || !profile.departureDestination) {
      result.push(rule("departure-details", {
        title: "Add your departure date and destination country",
        domain: "Residency",
        requirement: "recommended",
        summary: "Destination and timing change health insurance, pension, tax, permit and vehicle steps.",
        why: "A generic leaving reminder cannot safely distinguish EU/EFTA coordination from a move to another country.",
        impact: "Unlocks a sequenced departure plan",
        effort: "2 minutes",
        dueDate: addDays(today, 7),
        dueLabel: "Complete the departure details",
        confidence: "needs-input",
        dateKind: "input",
        logic: "Leaving Switzerland is selected, but date or destination is missing.",
        documents: [],
        steps: [{ id: "profile", title: "Add the permanent departure date and destination country in Profile" }],
        sources: [sources.leaving, sources.healthAbroad],
      }));
    } else {
      const prepDate = addDays(profile.eventDate, -60) < today ? today : addDays(profile.eventDate, -60);
      result.push(rule("permanent-departure-plan", {
        title: `Prepare your permanent move to ${profile.departureDestination}`,
        domain: "Residency",
        requirement: "required",
        summary: `Work backwards from ${profile.eventDate}; destination-specific rules must be confirmed before pension or insurance decisions.`,
        why: "Deregistration, tax, health cover, pensions, banking and vehicle administration do not all follow the same rule.",
        impact: "Creates one controlled departure sequence",
        effort: "Several sessions",
        dueDate: prepDate,
        dueLabel: "Start about two months before permanent departure",
        dateKind: "review",
        logic: "A permanent departure date and destination are recorded. CH/OS avoids assuming EU/EFTA status from a country name.",
        documents: ["Commune deregistration requirements", "Employment-end evidence", "Tax and pension statements", "Insurance policies", "Vehicle documents"],
        steps: [
          { id: "commune", title: "Get the commune's deregistration instructions and confirmation requirements" },
          { id: "tax", title: "Confirm the departure-tax filing and address route with the canton" },
          { id: "health", title: "Get a written decision on when Swiss health and accident cover ends or must continue" },
          { id: "pensions", title: "Ask each pension institution what can remain, transfer or be withdrawn for this destination" },
          { id: "vehicle", title: "Handle plates, registration, licence and customs for any vehicle" },
          { id: "providers", title: "Update banks, insurers, employer and essential contracts with the foreign address" },
          { id: "backup", title: "Export your CH/OS data before changing devices or browser storage" },
        ],
        sources: [sources.leaving, sources.healthAbroad, sources.pillar2, sources.pillar3a],
      }));
    }
  }

  if (profile.housing === "owner" && profile.mortgageMaturityDate) {
    const reviewDate = addDays(profile.mortgageMaturityDate, -180);
    result.push(rule("mortgage-renewal-plan", {
      title: "Prepare your mortgage maturity decision",
      domain: "Home",
      requirement: "recommended",
      summary: `Your recorded mortgage maturity is ${profile.mortgageMaturityDate}. Start with affordability, liquidity and ownership evidence rather than a product ranking.`,
      why: "This is a user-defined contract date, not a statutory deadline.",
      impact: "Creates time to compare structure and negotiate",
      effort: "2–4 hours",
      dueDate: reviewDate < today ? today : reviewDate,
      dueLabel: "Planning date: six months before recorded maturity",
      dateKind: "review",
      logic: "A mortgage maturity date is recorded. CH/OS does not recommend a lender or rate structure.",
      documents: ["Mortgage contracts", "Current affordability figures", "Tax return", "Pension pledge or withdrawal records", "Co-ownership details"],
      steps: [
        { id: "contract", title: "Confirm maturity, notice and early-exit terms in every tranche" },
        { id: "affordability", title: "Refresh income, expenses, maintenance and liquidity assumptions" },
        { id: "ownership", title: "Confirm borrower, ownership-share and pension-pledge records" },
        { id: "compare", title: "Request comparable written offers before committing" },
      ],
      sources: [sources.realEstateTax],
    }));
  }

  if (profile.housing === "owner" && profile.renovationStartDate) {
    const checkDate = addDays(profile.renovationStartDate, -90);
    result.push(rule("renovation-authority-check", {
      title: "Check permits and subsidies before renovation contracts are fixed",
      domain: "Home",
      requirement: "recommended",
      summary: `Your planned works start on ${profile.renovationStartDate}; cantonal and communal rules can affect permits and support.`,
      why: "Some subsidy routes require an application or energy assessment before work begins.",
      impact: "Avoids missed approvals or support windows",
      effort: "1–3 hours",
      dueDate: checkDate < today ? today : checkDate,
      dueLabel: "Planning date: three months before works",
      dateKind: "review",
      logic: "A user-defined renovation start date is recorded; no permit or subsidy eligibility is inferred.",
      documents: ["Scope of works", "Plans and quotes", "Building and ownership records", "Energy assessment if relevant"],
      steps: [
        { id: "commune", title: "Ask the commune which works need notification or a permit" },
        { id: "canton", title: "Check the cantonal Buildings Programme before signing or starting work" },
        { id: "tax", title: "Separate maintenance from value-adding works for tax evidence" },
        { id: "contracts", title: "Keep scope, quotes, approvals, invoices and warranties together" },
      ],
      sources: [sources.buildingsProgramme, sources.realEstateTax],
    }));
  }

  if (profile.homePurchaseDate) {
    const prepDate = addDays(profile.homePurchaseDate, -90);
    result.push(rule("home-purchase-plan", {
      title: "Prepare the ownership, financing and tax file for your home purchase",
      domain: "Home",
      requirement: "recommended",
      summary: `Your recorded purchase or transfer date is ${profile.homePurchaseDate}; use it to sequence ownership, financing, pension and cantonal checks.`,
      why: "Ownership form, land-register shares, pension funding and property taxes should agree before the contract is final.",
      impact: "Reduces expensive ownership and documentation mistakes",
      effort: "Several sessions",
      dueDate: prepDate < today ? today : prepDate,
      dueLabel: "Planning date: about three months before purchase",
      dateKind: "review",
      logic: `Ownership form is ${profile.ownershipForm ?? "not recorded"}; pension money used is ${profile.pensionUsedForHome ?? "unknown"}.`,
      documents: ["Draft purchase contract", "Financing offers", "Land-register ownership proposal", "Pension pledge or withdrawal documents", "Tax and fee estimate"],
      steps: [
        { id: "ownership", title: "Confirm sole, co-, joint or condominium ownership and exact shares" },
        { id: "financing", title: "Compare written financing offers on the same assumptions" },
        { id: "pension", title: "If pension assets are involved, document withdrawal or pledge consequences" },
        { id: "tax", title: "Estimate transfer fees, property taxes and the future tax-return evidence route" },
        { id: "reserve", title: "Keep renovation, maintenance and liquidity reserves outside the purchase price" },
      ],
      sources: [sources.homeOwnership, sources.realEstateTax, sources.pillar2, sources.pillar3a],
    }));
  }

  if (profile.housing === "owner" && profile.amortizationReviewDate) {
    result.push(rule("mortgage-amortization-review", {
      title: "Review mortgage amortisation against liquidity and pension plans",
      domain: "Home",
      requirement: "optional",
      summary: `Use ${profile.amortizationReviewDate} as your own decision date, not as a universal repayment rule.`,
      why: "Debt reduction, liquidity, taxes and retirement assets interact; CH/OS does not prescribe one optimal structure.",
      impact: "Makes a large balance-sheet decision explicit",
      effort: "1–2 hours",
      dueDate: profile.amortizationReviewDate < today ? today : profile.amortizationReviewDate,
      dueLabel: "User-defined review date",
      dateKind: "review",
      logic: "A mortgage-amortisation review date is recorded.",
      documents: ["Mortgage schedule", "Liquidity plan", "Pension statements", "Tax estimate", "Planned renovation costs"],
      steps: [
        { id: "contract", title: "Confirm contractual direct or indirect amortisation requirements" },
        { id: "liquidity", title: "Stress-test cash after any voluntary repayment" },
        { id: "pension", title: "Check how indirect amortisation affects pension concentration and access" },
        { id: "tax", title: "Model tax effects without assuming deductions make debt desirable" },
      ],
      sources: [sources.realEstateTax, sources.pillar3a],
    }));
  }

  if (profile.serviceType && profile.serviceType !== "none") {
    if (!profile.serviceStartDate || !profile.serviceEndDate) {
      result.push(rule("service-eo-details", {
        title: "Add your service period to build the EO claim route",
        domain: "Work",
        requirement: "recommended",
        summary: "Military, civil, civil-protection and eligible course service can create an income-compensation claim.",
        why: "From 2026, service EO claims move through a digital process and routing still depends on employment status.",
        impact: "Unlocks a dated service checklist",
        effort: "2 minutes",
        dueDate: addDays(today, 7),
        dueLabel: "Complete the service dates",
        confidence: "needs-input",
        dateKind: "input",
        logic: `${profile.serviceType} is selected, but the start or end date is missing.`,
        documents: ["Service notice or course confirmation"],
        steps: [{ id: "dates", title: "Add the service start and end dates in Profile" }],
        sources: [sources.eoService],
      }));
    } else {
      const prepDate = addDays(profile.serviceStartDate, -14);
      if (profile.serviceStartDate >= today) {
        result.push(rule("service-eo-preparation", {
          title: "Prepare salary and routing facts for your EO claim",
          domain: "Work",
          requirement: "recommended",
          summary: `Your ${profile.serviceType} period begins on ${profile.serviceStartDate}.`,
          why: "Employees, self-employed people, students and people with several employers follow different evidence routes.",
          impact: "Reduces claim delays",
          effort: "20–40 minutes",
          dueDate: prepDate < today ? today : prepDate,
          dueLabel: "Before service starts",
          dateKind: "review",
          logic: `EO claim status is ${profile.eoClaimStatus ?? "not recorded"}.`,
          documents: ["Service notice", "Employer list", "Pre-service salary evidence", "Compensation-office details"],
          steps: [
            { id: "employers", title: "Confirm every employer and pre-service salary record" },
            { id: "route", title: "Confirm whether the digital claim routes through an employer or compensation office" },
            { id: "contact", title: "Keep the mobile number and email used for the EO portal current" },
          ],
          sources: [sources.eoService, sources.compensationOffices],
        }));
      }
      if (profile.eoClaimStatus !== "submitted" && profile.eoClaimStatus !== "paid") {
        const claimDate = addDays(profile.serviceEndDate, 7);
        result.push(rule("service-eo-claim", {
          title: "Complete and track your EO service claim",
          domain: "Work",
          requirement: "required",
          summary: `Your service ends on ${profile.serviceEndDate}; use the portal notification to complete the compensation claim.`,
          why: "Service days are transmitted digitally from 2026, but the person and employer still need to complete their parts.",
          impact: "Claims income compensation",
          effort: "15–45 minutes",
          dueDate: claimDate < today ? today : claimDate,
          dueLabel: "Complete promptly after the service period",
          dateKind: "review",
          logic: `EO claim status is ${profile.eoClaimStatus ?? "not started"}.`,
          documents: ["EO portal message", "Salary and employer details", "Bank payment details supplied directly to the authority"],
          steps: [
            { id: "portal", title: "Open the official EO portal message and verify the service days" },
            { id: "employment", title: "Complete the employment or student status section" },
            { id: "employers", title: "Ensure every employer completes its required salary certification" },
            { id: "submit", title: "Submit and retain the claim confirmation" },
            { id: "track", title: "Record submitted or paid status in CH/OS" },
          ],
          sources: [sources.eoService, sources.compensationOffices],
        }));
      }
    }
  }

  if (profile.employment === "caregiver") {
    if (profile.spouseContributionThresholdMet === undefined) {
      result.push(rule("caregiver-spouse-contribution-details", {
        title: "Check whether a working spouse covers the AHV minimum route",
        domain: "Pension",
        requirement: "recommended",
        summary: "A non-employed spouse may not owe a separate contribution when the working spouse pays at least twice the minimum.",
        why: "Care credits do not themselves remove the non-employed contribution obligation.",
        impact: "Avoids both contribution gaps and unnecessary duplicate payments",
        effort: "15–30 minutes",
        dueDate: addDays(today, 30),
        dueLabel: "Verify with the compensation office",
        dateKind: "review",
        logic: "Caregiver status is recorded, but the spouse-contribution threshold is unknown.",
        documents: ["Marital status", "Spouse contribution or salary evidence", "Any compensation-office decision"],
        steps: [
          { id: "evidence", title: "Confirm the working spouse's annual AHV contributions" },
          { id: "office", title: "Ask the compensation office whether the twice-minimum exception applies" },
          { id: "decision", title: "Keep the written assessment and later verify the AHV account" },
        ],
        sources: [sources.nonEmployedAhv, sources.compensationOffices],
      }));
    }
    if (profile.caresForDependentRelative === "yes" && profile.careCreditClaimed !== "yes") {
      result.push(rule("care-credit-annual-claim", {
        title: "Check and claim the annual AHV care credit",
        domain: "Pension",
        requirement: "recommended",
        summary: "Care credits for an eligible close relative must be claimed for each year.",
        why: "The credit is not an automatic cash payment and does not replace contribution obligations.",
        impact: "May improve the later AHV calculation",
        effort: "30–60 minutes",
        dueDate: addDays(today, 30),
        dueLabel: "Verify and claim for the current care year",
        dateKind: "review",
        logic: "Care for a dependent relative is recorded and an annual care-credit claim is not confirmed.",
        documents: ["Relationship and care evidence", "Helplessness-allowance evidence", "Residence-distance evidence if requested"],
        steps: [
          { id: "eligibility", title: "Check the official relative, helplessness-allowance and care conditions" },
          { id: "office", title: "Request the current-year form from the compensation office" },
          { id: "claim", title: "Submit the annual claim with the requested evidence" },
          { id: "record", title: "Save confirmation and mark the claim year in CH/OS" },
        ],
        sources: [sources.careCredits, sources.compensationOffices],
      }));
    }
  }

  const dependants = profile.householdMembers?.filter((member) => member.role === "dependent") ?? [];
  const currentYear = Number(today.slice(0, 4));
  const premiumReductionWorthChecking = profile.incomeBand === "under-80"
    || dependants.length >= 2
    || profile.employment === "student"
    || profile.employment === "caregiver"
    || profile.employment === "unemployed";
  if (
    profile.livesInSwitzerland !== "no"
    && premiumReductionWorthChecking
    && (
      !profile.premiumReductionReviewDate
      || Number(profile.premiumReductionReviewDate.slice(0, 4)) < currentYear
    )
  ) {
    result.push(rule(`premium-reduction-${currentYear}`, {
      title: "Check this year's cantonal health-premium reduction",
      domain: "Health",
      requirement: "recommended",
      summary: "Eligibility, automatic notification and application timing differ by canton.",
      why: "Lower-income residents and households with several children may qualify, but some cantons require a new application.",
      impact: "May reduce a major recurring household cost",
      effort: "20–45 minutes",
      dueDate: addDays(today, 21),
      dueLabel: "Check the current cantonal route within three weeks",
      confidence: "review-window",
      dateKind: "review",
      deadlineKind: "authority",
      logic: `A possible signal is recorded (${profile.incomeBand ?? "income not stated"}, ${dependants.length} dependants, ${profile.employment ?? "work status unknown"}), but CH/OS does not infer eligibility.`,
      documents: ["Current cantonal tax assessment if requested", "Household and child details", "Health-insurance policies"],
      steps: [
        { id: "authority", title: "Open the competent cantonal premium-reduction office" },
        { id: "automatic", title: "Confirm whether your canton assesses automatically or requires an application" },
        { id: "deadline", title: "Record the canton-specific deadline before submitting" },
        { id: "submit", title: "Apply with the requested household evidence if eligible" },
        { id: "record", title: "Record the completed review date in Profile" },
      ],
      sources: [sources.premiumReduction],
    }));
  }
  if (dependants.length > 0 && profile.familyAllowanceClaimed !== "yes") {
    result.push(rule("family-allowance-claim", {
      title: "Confirm and submit the household's family-allowance claim",
      domain: "Money",
      requirement: "recommended",
      summary: `${dependants.length} dependant${dependants.length === 1 ? "" : "s"} are recorded; entitlement and priority between parents still require an official decision.`,
      why: "Family allowances use a federal framework with cantonal amounts and are not simply activated by adding a child to CH/OS.",
      impact: "May secure recurring child or education allowances",
      effort: "30–60 minutes",
      dueDate: addDays(today, 30),
      dueLabel: "Apply through the employer or compensation office",
      dateKind: "review",
      logic: "At least one dependant is recorded and a family-allowance claim is not confirmed.",
      documents: ["Child and education evidence", "Both parents' employment details", "Existing allowance decisions"],
      steps: [
        { id: "priority", title: "Identify which parent or employer has first claim priority" },
        { id: "route", title: "Request the cantonal/employer application form" },
        { id: "submit", title: "Submit the claim and supporting evidence" },
        { id: "coordination", title: "Disclose any allowance paid by another employer or country" },
      ],
      sources: [sources.familyAllowances, sources.compensationOffices],
    }));
  }
  for (const dependant of dependants) {
    if (!dependant.birthYear) continue;
    const age = currentYear - dependant.birthYear;
    if (age < 3 || age > 18) continue;
    if (
      dependant.schoolAdminReviewedDate
      && Number(dependant.schoolAdminReviewedDate.slice(0, 4)) >= currentYear
    ) continue;
    const name = dependant.label?.trim() || "your dependant";
    result.push(rule(`school-admin-${dependant.id}-${currentYear}`, {
      title: `Review local school and commune administration for ${name}`,
      domain: "Belonging",
      requirement: "recommended",
      summary: "Confirm registration, the next school-year transition, language or learning support and the correct local contact.",
      why: "Compulsory education is cantonal and schools are organised locally, so a national age estimate cannot safely supply the final deadline.",
      impact: "Prevents missed local notices and support opportunities",
      effort: "20–45 minutes",
      dueDate: addDays(today, 30),
      dueLabel: "Annual local administration review",
      confidence: "review-window",
      dateKind: "review",
      deadlineKind: "authority",
      logic: `${name} is approximately ${age}; no school/commune administration review is recorded for ${currentYear}.`,
      documents: ["School or commune notices", "Residence details", "Relevant language or support decisions"],
      steps: [
        { id: "contact", title: "Confirm the competent commune or school administration" },
        { id: "transition", title: "Check registration, school-year and transition dates" },
        { id: "support", title: "Ask about language, care or learning support where relevant" },
        { id: "calendar", title: "Add confirmed local dates as personal actions" },
        { id: "record", title: "Record the completed review date in the household member profile" },
      ],
      sources: [sources.compulsorySchool, sources.publicAuthorities],
    }));
  }

  if (profile.childcareApplicationDate) {
    result.push(rule("childcare-application", {
      title: "Complete the childcare or subsidy application",
      domain: "Money",
      requirement: "recommended",
      summary: "This is your recorded local application date; eligibility and waiting lists remain municipal or cantonal.",
      why: "Childcare applications can have local lead times that are not safe to infer nationally.",
      impact: "Protects a user-defined family logistics date",
      effort: "30–90 minutes",
      dueDate: profile.childcareApplicationDate < today ? today : profile.childcareApplicationDate,
      dueLabel: "User-defined local application date",
      dateKind: "review",
      logic: "A childcare application date is recorded.",
      documents: ["Child and household details", "Employment schedules", "Income evidence requested by the local authority"],
      steps: [
        { id: "provider", title: "Confirm provider availability and required start date" },
        { id: "authority", title: "Check the commune or canton subsidy route" },
        { id: "submit", title: "Submit the application and keep confirmation" },
        { id: "followup", title: "Add the decision or waiting-list follow-up date" },
      ],
      sources: [sources.publicAuthorities],
    }));
  }

  const otherAdults = profile.householdMembers?.filter((member) => member.role !== "dependent") ?? [];
  for (const member of otherAdults) {
    if (member.employment !== "employed" && member.employment !== "self-employed") continue;
    if (member.pensionFundMember !== undefined && member.pillar3aContributed !== undefined) continue;
    const name = member.label?.trim() || (member.role === "partner" ? "your partner" : "the other adult");
    result.push(rule(`household-member-pension-${member.id}`, {
      title: `Complete the separate pension baseline for ${name}`,
      domain: "Pension",
      requirement: "recommended",
      summary: "CH/OS keeps this person's pension and 3a facts separate from yours and does not combine contribution limits.",
      why: "A household label must not make one adult's pension action appear to cover another adult.",
      impact: "Prevents person-mixing in household planning",
      effort: "10 minutes",
      dueDate: addDays(today, 30),
      dueLabel: "Complete the household-member record",
      confidence: "needs-input",
      dateKind: "input",
      logic: `${name} has paid work recorded, but pension-fund or 3a information is incomplete.`,
      documents: ["That person's own pension certificate and 3a contribution total"],
      steps: [{ id: "profile", title: `Complete pension-fund and 3a fields for ${name}` }],
      sources: [sources.pillar2, sources.pillar3a],
    }));
  }

  const protectionNeedsWork = profile.patientDirectiveStatus === "none"
    || profile.patientDirectiveStatus === "draft"
    || profile.patientDirectiveStatus === "review-due"
    || profile.advanceCareMandateStatus === "none"
    || profile.advanceCareMandateStatus === "draft"
    || profile.advanceCareMandateStatus === "review-due"
    || profile.willStatus === "none"
    || profile.willStatus === "draft"
    || profile.willStatus === "review-due";
  if (protectionNeedsWork) {
    result.push(rule("legal-protection-file", {
      title: "Complete the documents that let others act when you cannot",
      domain: "Protection",
      requirement: "recommended",
      summary: "Review patient wishes, representation, estate intentions, beneficiaries and where signed originals can be found.",
      why: "These documents solve different problems and should not be treated as one generic emergency folder.",
      impact: "Reduces family and decision friction during incapacity or death",
      effort: "Several sessions",
      dueDate: addDays(today, 60),
      dueLabel: "Complete or review within two months",
      dateKind: "review",
      logic: `Patient directive: ${profile.patientDirectiveStatus ?? "unknown"}; advance-care mandate: ${profile.advanceCareMandateStatus ?? "unknown"}; will: ${profile.willStatus ?? "unknown"}.`,
      documents: ["Signed originals stored outside CH/OS", "Professional advice where needed", "Beneficiary confirmations"],
      steps: [
        { id: "patient", title: "Complete or review the signed patient directive and named medical contact" },
        { id: "mandate", title: "Verify the required form for an advance-care mandate" },
        { id: "will", title: "Use qualified advice where estate or family circumstances are complex" },
        { id: "beneficiaries", title: "Review pension and insurance beneficiary nominations separately" },
        { id: "access", title: "Tell trusted people where signed originals and access instructions are stored" },
      ],
      sources: [sources.patientDirective, sources.publicAuthorities],
    }));
  }

  if (
    (profile.household === "couple" || profile.household === "family")
    && (
      !profile.beneficiariesReviewedDate
      || addYears(profile.beneficiariesReviewedDate, 2) <= today
    )
  ) {
    result.push(rule("beneficiary-review", {
      title: "Review pension, insurance and account beneficiaries",
      domain: "Protection",
      requirement: "recommended",
      summary: "Household and family changes can leave beneficiary nominations out of sync with current intentions.",
      why: "Beneficiary rules differ across pension, insurance and estate arrangements.",
      impact: "Aligns important survivor instructions",
      effort: "30–60 minutes",
      dueDate: addDays(today, 30),
      dueLabel: "Review within a month",
      dateKind: "review",
      logic: profile.beneficiariesReviewedDate
        ? `The last recorded review was ${profile.beneficiariesReviewedDate}.`
        : "No beneficiary-review date is recorded for a multi-person household.",
      documents: ["Pension-fund beneficiary rules", "Insurance nominations", "Estate documents", "Account payable-on-death rules if any"],
      steps: [
        { id: "pension", title: "Check Pillar 2 and 3a beneficiary rules and nominations" },
        { id: "insurance", title: "Check life and accident-insurance beneficiaries" },
        { id: "estate", title: "Compare nominations with the current estate plan" },
        { id: "date", title: "Record the completed review date in CH/OS" },
      ],
      sources: [sources.pillar2, sources.pillar3a, sources.publicAuthorities],
    }));
  }

  if (profile.usTaxLink === "yes" || profile.equityCompensation === "yes") {
    result.push(rule("complex-compensation-tax-check", {
      title: "Build a verification file for cross-border tax or equity compensation",
      domain: "Tax",
      requirement: "recommended",
      summary: "Keep each person's income, awards, accounts and tax residence separate before seeking qualified advice.",
      why: "CH/OS cannot infer US filing status, treaty treatment or equity-compensation taxation from a yes/no marker.",
      impact: "Reduces omitted reporting and person-mixing risk",
      effort: "1–2 hours",
      dueDate: addDays(today, 30),
      dueLabel: "Before the next filing or vesting decision",
      dateKind: "review",
      logic: `US tax link: ${profile.usTaxLink ?? "unknown"}; equity compensation: ${profile.equityCompensation ?? "unknown"}.`,
      documents: ["Separate person-level tax residences", "Award and vesting statements", "Broker and bank statements", "Prior filings"],
      steps: [
        { id: "people", title: "Separate records and obligations by person" },
        { id: "events", title: "List grant, vest, exercise, sale and withholding events" },
        { id: "official", title: "Review the official Swiss international-reporting context" },
        { id: "advice", title: "Use a qualified cross-border adviser for filing and treaty conclusions" },
      ],
      sources: [sources.fatca],
    }));
  }
  return result;
}

export function buildPrecisionRules(state: AppState, today: string): RuleDefinition[] {
  return [
    ...vehicleRules(state.profile, today),
    ...residenceRules(state.profile, today),
    ...workAndInsuranceRules(state.profile, today),
    ...taxAndPensionRules(state.profile, today),
    ...healthRules(state.profile),
    ...homeRules(state.profile, today),
    ...moneyRules(state.profile, today),
    ...lifecycleRules(state.profile, today),
  ];
}

export const precisionSuppressedRuleIds = new Set([
  "tax-at-source-check",
  "pillar-stack",
  "ahv-record",
  "mobility-review",
  "work-cover",
  "rent-review",
  "cash-buffer",
]);
